// @ts-nocheck
import { useState, useRef, useEffect } from "react";
import { useDashboardApi } from "../../store/api/hooks/dashboardApiHook";
import {
  ResponsiveContainer,
  FunnelChart,
  Funnel,
  Cell,
  LabelList,
  Tooltip,
  RadialBarChart,
  RadialBar,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  PieChart,
  Pie,
  LineChart,
  Line,
  Legend,
  ScatterChart,
  Scatter,
  ZAxis,
  ReferenceLine,
  ReferenceArea,
} from "recharts";
import Layout from '../../components/layout';
import Header from '../../components/header';
import { downloadExcel, downloadPDF } from "../../_common/functions";
import { Dropdown, Menu } from "../../components/Dropdown";

type TimePeriod = "7D" | "30D" | "120D" | "12Mo";

const tabs = [
  "Work Requests & Tasks",
  "Team Metrics",
  "Purchasing",
  "Asset Metrics",
];

const agingColumns = ["1 Day", "2 Days", "3 Days", "4-7 Days", "7-10 Days", "10-15 Days", "15+ Days", "Total"];

const agingData = [
  { priority: "Critical", badge: { bg: "#fee2e2", text: "#dc2626" }, values: [2, 1, 3, 4, 2, 1, 3, 16] },
  { priority: "High", badge: { bg: "#ffedd5", text: "#ea580c" }, values: [4, 3, 5, 8, 4, 3, 6, 33] },
  { priority: "Medium", badge: { bg: "#fefce8", text: "#ca8a04" }, values: [6, 5, 8, 12, 7, 5, 9, 52] },
  { priority: "Low", badge: { bg: "#eff6ff", text: "#2563eb" }, values: [3, 4, 6, 9, 5, 4, 7, 38] },
];

const agingTotals = [15, 13, 22, 33, 18, 13, 25, 139];

const funnelData = [
  { name: "New", value: 210, fill: "#2B5A2E" },
  { name: "Assigned", value: 168, fill: "#3D7A3E" },
  { name: "Scheduled", value: 124, fill: "#16a34a" },
  { name: "In Progress", value: 89, fill: "#86efac" },
];

const highPriorityWRs = [
  { description: "Ventilation fan failure", facility: "Seahawk Farm LLC / Barn 3", requester: "Jake R.", asset: "Fan Cover Project", priority: "Critical" },
  { description: "Water line leak in residence", facility: "Wolfpack Farm LLC / Residence 1", requester: "Maria S.", asset: "Washer-Residence 1", priority: "High" },
  { description: "Heating system malfunction", facility: "Bobcat Farm LLC / Barn 7", requester: "Tom B.", asset: "Climate System Unit 2", priority: "Critical" },
  { description: "Generator not starting", facility: "Y24 Farm LLC / Main Facility", requester: "Carlos M.", asset: "Generator Unit 1", priority: "High" },
  { description: "Feed auger jammed", facility: "Tar Heel Farm LLC / Barn 2", requester: "Anna K.", asset: "Feed System A", priority: "High" },
  { description: "Curtain motor failure", facility: "Bugeater Farm LLC / Barn 5", requester: "Dave L.", asset: "Curtain System B", priority: "Critical" },
  { description: "Alarm panel offline", facility: "Wildcat Farm LLC / Control Room", requester: "Sam W.", asset: "Alarm Panel 3", priority: "High" },
  { description: "Cooling pad pump seized", facility: "Lancelot Farm LLC / Barn 1", requester: "Lisa P.", asset: "Evap Cooler Unit 4", priority: "High" },
  { description: "Propane regulator leak", facility: "Razorback Farm LLC / Barn 4", requester: "Jake R.", asset: "Heater System C", priority: "Critical" },
  { description: "Conveyor belt slipping", facility: "Volunteer Farm LLC / Processing", requester: "Maria S.", asset: "Belt Conveyor 2", priority: "High" },
];

const priorityBadgeStyles: Record<string, { bg: string; text: string }> = {
  Critical: { bg: "#fee2e2", text: "#dc2626" },
  High: { bg: "#ffedd5", text: "#ea580c" },
};

const radialData = [
  { name: "Low", value: 94, fill: "#2563eb" },
  { name: "Medium", value: 189, fill: "#ca8a04" },
  { name: "High", value: 112, fill: "#ea580c" },
  { name: "Critical", value: 47, fill: "#dc2626" },
];

const taskCreationTimeData = [
  { bucket: "<2 hrs", count: 18 },
  { bucket: "2-4 hrs", count: 34 },
  { bucket: "4-8 hrs", count: 45 },
  { bucket: "8-24 hrs", count: 38 },
  { bucket: "1-2 days", count: 27 },
  { bucket: "2-5 days", count: 19 },
  { bucket: ">5 days", count: 12 },
];

const taskCompletionTimeData = [
  { bucket: "<4 hrs", count: 12 },
  { bucket: "4-8 hrs", count: 28 },
  { bucket: "8-24 hrs", count: 52 },
  { bucket: "1-2 days", count: 47 },
  { bucket: "2-5 days", count: 34 },
  { bucket: "5-10 days", count: 21 },
  { bucket: ">10 days", count: 9 },
];

const renderFunnelLabel = (props: any) => {
  const { x, y, width, height, name, value } = props;
  const textColor = name === "In Progress" ? "#111827" : "#ffffff";
  return (
    <text
      x={x + width / 2}
      y={y + height / 2}
      textAnchor="middle"
      dominantBaseline="central"
      fill={textColor}
      fontWeight={600}
      fontSize={13}
    >
      {name} — {value}
    </text>
  );
};

function ChartCard({ title, subtitle, rightHeader, className, children }: { title: string; subtitle: string; rightHeader?: React.ReactNode; className?: string; children: React.ReactNode }) {
  return (
    <div
      className={`bg-white rounded-lg flex flex-col ${className || ""}`}
      style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}
    >
      <div className="px-5 pt-4 pb-3 shrink-0 flex items-start justify-between">
        <div>
          <h3 className="text-[15px] font-semibold text-[#111827]">{title}</h3>
          <p className="text-[12px] text-[#9ca3af] mt-0.5">{subtitle}</p>
        </div>
        {rightHeader && <div className="shrink-0">{rightHeader}</div>}
      </div>
      <div className="flex-1 min-h-0 px-5 pb-4" style={{ overflowY: "scroll",maxHeight:735 }}>{children}</div>
    </div>
  );
}

const priorityBadgeConfig: Record<string, { bg: string; text: string }> = {
  "Immediate mortality risk": { bg: "#fee2e2", text: "#dc2626" },
  "Immediate flock performance impact": { bg: "#ffedd5", text: "#ea580c" },
  "Ok to fix between flocks": { bg: "#fefce8", text: "#ca8a04" },
  "Preventative": { bg: "#eff6ff", text: "#2563eb" },
};

const wrPriorityColors: Record<string, string> = {
  "Immediate mortality risk": "#dc2626",
  "Immediate flock performance impact": "#ea580c",
  "Ok to fix between flocks": "#ca8a04",
  "Preventative": "#2563eb",
};

const wrPriorityOrder = [
  "Immediate mortality risk",
  "Immediate flock performance impact",
  "Ok to fix between flocks",
  "Preventative",
];

const RESPONSE_BUCKET_ORDER = ["<4 hrs", "4-8 hrs", "8-24 hrs", "1-2 days", "2-5 days", "5-10 days", ">10 days"];

const funnelColors = ["#2B5A2E", "#3D7A3E", "#16a34a", "#86efac"];

function WRTabLoader() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="flex flex-col items-center gap-3">
        <div className="relative w-10 h-10">
          <div className="absolute inset-0 rounded-full border-[3px] border-[#e5e7eb]" />
          <div className="absolute inset-0 rounded-full border-[3px] border-transparent border-t-[#2B5A2E] animate-spin" />
        </div>
        <span className="text-[11px] text-[#9ca3af] font-medium">Loading data...</span>
      </div>
    </div>
  );
}

type WRTimePeriod = "1W" | "3W" | "30D" | "3Mo" | "6Mo" | "12Mo";
const wrPeriodLabels: Record<WRTimePeriod, string> = {
  "1W": "Last 1 week", "3W": "Last 3 weeks", "30D": "Last 30 days", "3Mo": "Last 3 months", "6Mo": "Last 6 months", "12Mo": "Last 12 months",
};
const wrPeriodToDays: Record<WRTimePeriod, number> = {
  "1W": 7, "3W": 21, "30D": 30, "3Mo": 90, "6Mo": 180, "12Mo": 365,
};

function WRTimeToggle({ active, onChange }: { active: WRTimePeriod; onChange: (p: WRTimePeriod) => void }) {
  const periods: WRTimePeriod[] = ["1W", "3W", "30D", "3Mo", "6Mo", "12Mo"];
  return (
    <div className="flex gap-0.5">
      {periods.map((p) => (
        <button
          key={p}
          onClick={() => onChange(p)}
          className={`px-2.5 py-1 text-[12px] font-medium rounded-md transition-colors ${active === p
              ? "bg-[#2B5A2E] text-white"
              : "bg-white border border-gray-300 text-gray-500 hover:bg-gray-50"
            }`}
        >
          {p}
        </button>
      ))}
    </div>
  );
}

function WorkRequestsTab({ days, timePeriod }: { days: number; timePeriod: WRTimePeriod }) {
  const dashboardApi = useDashboardApi();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isExportingXLS, setIsExportingXLS] = useState(false);
  const [isExportingAgingPDF, setIsExportingAgingPDF] = useState(false);
  const [isExportingAgingXLS, setIsExportingAgingXLS] = useState(false);
  const [isExportingWRPriorityPDF, setIsExportingWRPriorityPDF] = useState(false);
  const [isExportingWRPriorityXLS, setIsExportingWRPriorityXLS] = useState(false);
  const [isExportingFunnelPDF, setIsExportingFunnelPDF] = useState(false);
  const [isExportingFunnelXLS, setIsExportingFunnelXLS] = useState(false);
  const [isExportingHighPriWRPDF, setIsExportingHighPriWRPDF] = useState(false);
  const [isExportingHighPriWRXLS, setIsExportingHighPriWRXLS] = useState(false);
  const [isExportingCreationTimePDF, setIsExportingCreationTimePDF] = useState(false);
  const [isExportingCreationTimeXLS, setIsExportingCreationTimeXLS] = useState(false);
  const [isExportingCompletionTimePDF, setIsExportingCompletionTimePDF] = useState(false);
  const [isExportingCompletionTimeXLS, setIsExportingCompletionTimeXLS] = useState(false);
  const [isExportingWRsWithoutTaskByPriorityPDF, setIsExportingWRsWithoutTaskByPriorityPDF] = useState(false);
  const [isExportingWRsWithoutTaskByPriorityXLS, setIsExportingWRsWithoutTaskByPriorityXLS] = useState(false);
  const [isExportingKpiWRsWithoutTaskPDF, setIsExportingKpiWRsWithoutTaskPDF] = useState(false);
  const [isExportingKpiWRsWithoutTaskXLS, setIsExportingKpiWRsWithoutTaskXLS] = useState(false);
  const [isExportingKpiTasksWithoutAssignmentPDF, setIsExportingKpiTasksWithoutAssignmentPDF] = useState(false);
  const [isExportingKpiTasksWithoutAssignmentXLS, setIsExportingKpiTasksWithoutAssignmentXLS] = useState(false);
  const [isExportingKpiTasksWithoutSchedulePDF, setIsExportingKpiTasksWithoutSchedulePDF] = useState(false);
  const [isExportingKpiTasksWithoutScheduleXLS, setIsExportingKpiTasksWithoutScheduleXLS] = useState(false);
  const [isExportingKpiTasksNotCompletedPDF, setIsExportingKpiTasksNotCompletedPDF] = useState(false);
  const [isExportingKpiTasksNotCompletedXLS, setIsExportingKpiTasksNotCompletedXLS] = useState(false);
  const [kpiData, setKpiData] = useState<any>(null);
  const [wrsWithoutTask, setWrsWithoutTask] = useState<any[]>([]);
  const [schedPeriod, setSchedPeriod] = useState<number>(7);
  const [scheduledTasksData, setScheduledTasksData] = useState<any[]>([]);
  const [isExportingSchedPDF, setIsExportingSchedPDF] = useState(false);
  const [isExportingSchedXLS, setIsExportingSchedXLS] = useState(false);
  const [tasksWRsFacilityTasks, setTasksWRsFacilityTasks] = useState<any[]>([]);
  const [tasksWRsFacilityWRs, setTasksWRsFacilityWRs] = useState<any[]>([]);
  const [isExportingTasksWRsPDF, setIsExportingTasksWRsPDF] = useState(false);
  const [isExportingTasksWRsXLS, setIsExportingTasksWRsXLS] = useState(false);
  const [totalResponseTimeData, setTotalResponseTimeData] = useState<any[]>([]);
  const [isExportingTotalResponsePDF, setIsExportingTotalResponsePDF] = useState(false);
  const [isExportingTotalResponseXLS, setIsExportingTotalResponseXLS] = useState(false);

  useEffect(() => {
    setLoading(true);
    dashboardApi.getManagerDashboardWR(
      { Days: days },
      (response: any) => {
        const d = response?.data || response;
        setData(d);
        setLoading(false);
      },
      (error: any) => {
        console.error("Failed to fetch WR tab:", error);
        setLoading(false);
      }
    );
  }, [days]);

  useEffect(() => {
    dashboardApi.getManagerDashboardWRKPIs(
      { Days: days },
      (response: any) => { setKpiData(response?.data || response); },
      (error: any) => { console.error("Failed to fetch WR KPIs:", error); }
    );
  }, [days]);

  useEffect(() => {
    dashboardApi.getWRsWithoutTaskByPriority(
      { Days: days },
      (response: any) => {
        const d = response?.data || response;
        setWrsWithoutTask(Array.isArray(d) ? d : []);
      },
      (error: any) => { console.error("Failed to fetch WRs without task:", error); }
    );
  }, [days]);

  useEffect(() => {
    dashboardApi.getTasksAndWRsByFacility(
      { Days: days },
      (response: any) => {
        setTasksWRsFacilityTasks(response?.tasks || []);
        setTasksWRsFacilityWRs(response?.workRequests || []);
      },
      (error: any) => { console.error("Failed to fetch tasks/WRs by facility:", error); }
    );
  }, [days]);

  useEffect(() => {
    dashboardApi.getTotalResponseTime(
      { Days: days },
      (response: any) => {
        const d = response?.data || response;
        setTotalResponseTimeData(Array.isArray(d) ? d : []);
      },
      (error: any) => { console.error("Failed to fetch total response time:", error); }
    );
  }, [days]);

  useEffect(() => {
    dashboardApi.getScheduledTasksByFacility(
      { Days: schedPeriod },
      (response: any) => {
        const d = response?.data || response;
        setScheduledTasksData(Array.isArray(d) ? d : []);
      },
      (error: any) => { console.error("Failed to fetch scheduled tasks:", error); }
    );
  }, [schedPeriod]);

  const handleExportSchedPDF = () => {
    setIsExportingSchedPDF(true);
    dashboardApi.exportScheduledTasksDetailToPDF(
      { Days: schedPeriod },
      (resp: any) => { downloadPDF(resp, 'scheduled_tasks_detail'); setIsExportingSchedPDF(false); },
      () => { setIsExportingSchedPDF(false); }
    );
  };

  const handleExportSchedXLS = () => {
    setIsExportingSchedXLS(true);
    dashboardApi.exportScheduledTasksDetailToXLS(
      { Days: schedPeriod },
      (resp: any) => { downloadExcel(resp, 'scheduled_tasks_detail'); setIsExportingSchedXLS(false); },
      () => { setIsExportingSchedXLS(false); }
    );
  };

  const handleExportTasksWRsPDF = () => {
    setIsExportingTasksWRsPDF(true);
    dashboardApi.exportTasksAndWRsByPriorityToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'tasks_and_wrs_by_priority'); setIsExportingTasksWRsPDF(false); },
      () => { setIsExportingTasksWRsPDF(false); }
    );
  };

  const handleExportTasksWRsXLS = () => {
    setIsExportingTasksWRsXLS(true);
    dashboardApi.exportTasksAndWRsByPriorityToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'tasks_and_wrs_by_priority'); setIsExportingTasksWRsXLS(false); },
      () => { setIsExportingTasksWRsXLS(false); }
    );
  };

  const handleExportPDF = () => {
    setIsExportingPDF(true);
    dashboardApi.exportManagerWorkRequestsToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'manager_work_requests'); setIsExportingPDF(false); },
      () => { setIsExportingPDF(false); }
    );
  };

  const handleExportXLS = () => {
    setIsExportingXLS(true);
    dashboardApi.exportManagerWorkRequestsToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'manager_work_requests'); setIsExportingXLS(false); },
      () => { setIsExportingXLS(false); }
    );
  };

  const handleExportAgingPDF = () => {
    setIsExportingAgingPDF(true);
    dashboardApi.exportTasksAgingPriorityToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'tasks_aging_with_priority'); setIsExportingAgingPDF(false); },
      () => { setIsExportingAgingPDF(false); }
    );
  };

  const handleExportAgingXLS = () => {
    setIsExportingAgingXLS(true);
    dashboardApi.exportTasksAgingPriorityToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'tasks_aging_with_priority'); setIsExportingAgingXLS(false); },
      () => { setIsExportingAgingXLS(false); }
    );
  };

  const handleExportWRPriorityPDF = () => {
    setIsExportingWRPriorityPDF(true);
    dashboardApi.exportWRByPriorityToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'work_requests_by_priority'); setIsExportingWRPriorityPDF(false); },
      () => { setIsExportingWRPriorityPDF(false); }
    );
  };

  const handleExportWRPriorityXLS = () => {
    setIsExportingWRPriorityXLS(true);
    dashboardApi.exportWRByPriorityToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'work_requests_by_priority'); setIsExportingWRPriorityXLS(false); },
      () => { setIsExportingWRPriorityXLS(false); }
    );
  };

  const handleExportFunnelPDF = () => {
    setIsExportingFunnelPDF(true);
    dashboardApi.exportTaskFunnelToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'task_funnel'); setIsExportingFunnelPDF(false); },
      () => { setIsExportingFunnelPDF(false); }
    );
  };

  const handleExportFunnelXLS = () => {
    setIsExportingFunnelXLS(true);
    dashboardApi.exportTaskFunnelToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'task_funnel'); setIsExportingFunnelXLS(false); },
      () => { setIsExportingFunnelXLS(false); }
    );
  };

  const handleExportHighPriWRPDF = () => {
    setIsExportingHighPriWRPDF(true);
    dashboardApi.exportHighPriorityWRsToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'high_priority_work_requests'); setIsExportingHighPriWRPDF(false); },
      () => { setIsExportingHighPriWRPDF(false); }
    );
  };

  const handleExportHighPriWRXLS = () => {
    setIsExportingHighPriWRXLS(true);
    dashboardApi.exportHighPriorityWRsToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'high_priority_work_requests'); setIsExportingHighPriWRXLS(false); },
      () => { setIsExportingHighPriWRXLS(false); }
    );
  };

  const handleExportCreationTimePDF = () => {
    setIsExportingCreationTimePDF(true);
    dashboardApi.exportTaskCreationTimeToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'task_creation_time'); setIsExportingCreationTimePDF(false); },
      () => { setIsExportingCreationTimePDF(false); }
    );
  };

  const handleExportCreationTimeXLS = () => {
    setIsExportingCreationTimeXLS(true);
    dashboardApi.exportTaskCreationTimeToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'task_creation_time'); setIsExportingCreationTimeXLS(false); },
      () => { setIsExportingCreationTimeXLS(false); }
    );
  };

  const handleExportTotalResponsePDF = () => {
    setIsExportingTotalResponsePDF(true);
    dashboardApi.exportTotalResponseTimeToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'total_response_time'); setIsExportingTotalResponsePDF(false); },
      () => { setIsExportingTotalResponsePDF(false); }
    );
  };

  const handleExportTotalResponseXLS = () => {
    setIsExportingTotalResponseXLS(true);
    dashboardApi.exportTotalResponseTimeToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'total_response_time'); setIsExportingTotalResponseXLS(false); },
      () => { setIsExportingTotalResponseXLS(false); }
    );
  };

  const handleExportCompletionTimePDF = () => {
    setIsExportingCompletionTimePDF(true);
    dashboardApi.exportTaskCompletionTimeToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'task_completion_time'); setIsExportingCompletionTimePDF(false); },
      () => { setIsExportingCompletionTimePDF(false); }
    );
  };

  const handleExportCompletionTimeXLS = () => {
    setIsExportingCompletionTimeXLS(true);
    dashboardApi.exportTaskCompletionTimeToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'task_completion_time'); setIsExportingCompletionTimeXLS(false); },
      () => { setIsExportingCompletionTimeXLS(false); }
    );
  };

  const handleExportWRsWithoutTaskByPriorityPDF = () => {
    setIsExportingWRsWithoutTaskByPriorityPDF(true);
    dashboardApi.exportWRsWithoutTaskByPriorityToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'wrs_without_task_by_priority'); setIsExportingWRsWithoutTaskByPriorityPDF(false); },
      () => { setIsExportingWRsWithoutTaskByPriorityPDF(false); }
    );
  };
  const handleExportWRsWithoutTaskByPriorityXLS = () => {
    setIsExportingWRsWithoutTaskByPriorityXLS(true);
    dashboardApi.exportWRsWithoutTaskByPriorityToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'wrs_without_task_by_priority'); setIsExportingWRsWithoutTaskByPriorityXLS(false); },
      () => { setIsExportingWRsWithoutTaskByPriorityXLS(false); }
    );
  };

  const handleExportKpiWRsWithoutTaskPDF = () => {
    setIsExportingKpiWRsWithoutTaskPDF(true);
    dashboardApi.exportManagerKpiWRsWithoutTaskToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'kpi_wrs_without_task'); setIsExportingKpiWRsWithoutTaskPDF(false); },
      () => { setIsExportingKpiWRsWithoutTaskPDF(false); }
    );
  };
  const handleExportKpiWRsWithoutTaskXLS = () => {
    setIsExportingKpiWRsWithoutTaskXLS(true);
    dashboardApi.exportManagerKpiWRsWithoutTaskToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'kpi_wrs_without_task'); setIsExportingKpiWRsWithoutTaskXLS(false); },
      () => { setIsExportingKpiWRsWithoutTaskXLS(false); }
    );
  };

  const handleExportKpiTasksWithoutAssignmentPDF = () => {
    setIsExportingKpiTasksWithoutAssignmentPDF(true);
    dashboardApi.exportManagerKpiTasksWithoutAssignmentToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'kpi_tasks_without_assignment'); setIsExportingKpiTasksWithoutAssignmentPDF(false); },
      () => { setIsExportingKpiTasksWithoutAssignmentPDF(false); }
    );
  };
  const handleExportKpiTasksWithoutAssignmentXLS = () => {
    setIsExportingKpiTasksWithoutAssignmentXLS(true);
    dashboardApi.exportManagerKpiTasksWithoutAssignmentToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'kpi_tasks_without_assignment'); setIsExportingKpiTasksWithoutAssignmentXLS(false); },
      () => { setIsExportingKpiTasksWithoutAssignmentXLS(false); }
    );
  };

  const handleExportKpiTasksWithoutSchedulePDF = () => {
    setIsExportingKpiTasksWithoutSchedulePDF(true);
    dashboardApi.exportManagerKpiTasksWithoutScheduleToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'kpi_tasks_without_schedule'); setIsExportingKpiTasksWithoutSchedulePDF(false); },
      () => { setIsExportingKpiTasksWithoutSchedulePDF(false); }
    );
  };
  const handleExportKpiTasksWithoutScheduleXLS = () => {
    setIsExportingKpiTasksWithoutScheduleXLS(true);
    dashboardApi.exportManagerKpiTasksWithoutScheduleToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'kpi_tasks_without_schedule'); setIsExportingKpiTasksWithoutScheduleXLS(false); },
      () => { setIsExportingKpiTasksWithoutScheduleXLS(false); }
    );
  };

  const handleExportKpiTasksNotCompletedPDF = () => {
    setIsExportingKpiTasksNotCompletedPDF(true);
    dashboardApi.exportManagerKpiTasksNotCompletedToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'kpi_tasks_not_completed'); setIsExportingKpiTasksNotCompletedPDF(false); },
      () => { setIsExportingKpiTasksNotCompletedPDF(false); }
    );
  };
  const handleExportKpiTasksNotCompletedXLS = () => {
    setIsExportingKpiTasksNotCompletedXLS(true);
    dashboardApi.exportManagerKpiTasksNotCompletedToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'kpi_tasks_not_completed'); setIsExportingKpiTasksNotCompletedXLS(false); },
      () => { setIsExportingKpiTasksNotCompletedXLS(false); }
    );
  };

  if (loading) return <WRTabLoader />;

  const agingRows = data?.taskAging || [];
  const agingCols = ["1 Day", "2 Days", "3 Days", "4-7 Days", "7-10 Days", "10-15 Days", "15+ Days", "Total"];
  const agingTotalsCalc = agingCols.map((_, i) => agingRows.reduce((sum: number, r: any) => {
    const vals = [r.day1, r.day2, r.day3, r.day4to7, r.day7to10, r.day10to15, r.day15Plus, r.total];
    return sum + (vals[i] || 0);
  }, 0));

  const wrByPriority = (data?.wrByPriority || []).map((r: any, i: number) => ({
    name: r.priorityName,
    value: r.wrCount,
    fill: wrPriorityColors[r.priorityName] || ["#dc2626", "#ea580c", "#ca8a04", "#2563eb"][i % 4],
  }));
  const totalOpenWRs = wrByPriority.reduce((s: number, r: any) => s + r.value, 0);

  const highPriWRs = data?.highPriorityWRs || [];

  const taskFunnelData = (data?.taskFunnel || []).map((r: any, i: number) => ({
    name: r.statusName, value: r.taskCount, fill: funnelColors[i % funnelColors.length],
  }));

  const creationTimeData = (data?.taskCreationTime || []).map((r: any) => ({ bucket: r.bucket, count: r.taskCount }));
  const completionTimeData = (data?.taskCompletionTime || []).map((r: any) => ({ bucket: r.bucket, count: r.taskCount }));
  const _responseBucketMap: Record<string, any> = {};
  totalResponseTimeData.forEach((r: any) => {
    const b = r.bucket;
    if (!_responseBucketMap[b]) _responseBucketMap[b] = { bucket: b };
    _responseBucketMap[b][r.priorityName] = (_responseBucketMap[b][r.priorityName] || 0) + (r.taskCount || 0);
  });
  const totalResponseData = RESPONSE_BUCKET_ORDER
    .map(b => _responseBucketMap[b] ?? { bucket: b });

  const creationMedian = creationTimeData.length > 0 ? creationTimeData.reduce((max: any, r: any) => r.count > max.count ? r : max, creationTimeData[0])?.bucket : "";
  const completionMedian = completionTimeData.length > 0 ? completionTimeData.reduce((max: any, r: any) => r.count > max.count ? r : max, completionTimeData[0])?.bucket : "";
  const responseMedian = totalResponseData.length > 0
    ? totalResponseData.reduce((max: any, r: any) => {
        const rTotal = wrPriorityOrder.reduce((s: number, p: string) => s + (r[p] || 0), 0);
        const mTotal = wrPriorityOrder.reduce((s: number, p: string) => s + (max[p] || 0), 0);
        return rTotal > mTotal ? r : max;
      }, totalResponseData[0])?.bucket
    : "";

  const wrsWithoutTaskAgg: Record<string, number> = {};
  wrsWithoutTask.forEach((r: any) => {
    if (r.priorityName) wrsWithoutTaskAgg[r.priorityName] = (wrsWithoutTaskAgg[r.priorityName] || 0) + (r.wrCount || 0);
  });
  const wrsWithoutTaskChartData = Object.entries(wrsWithoutTaskAgg).map(([name, value], i) => ({
    name,
    value,
    fill: wrPriorityColors[name] || ["#dc2626", "#ea580c", "#ca8a04", "#2563eb"][i % 4],
  }));
  const totalWRsWithoutTask = wrsWithoutTaskChartData.reduce((s: number, r: any) => s + r.value, 0);

  return (
    <div className="flex flex-col gap-5">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* KPI 1: WRs Without Task */}
        <div className="bg-white rounded-lg flex flex-col" style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
          <div className="flex items-start justify-between gap-2 px-5 pt-4 pb-2">
            <p style={{ fontSize: 13, fontWeight: 500, color: "#6b7280", lineHeight: 1.3 }}>WRs Without Task</p>
            <Dropdown position="end">
              <Dropdown.Button>
                <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff", whiteSpace: "nowrap" }}>
                  Export
                </button>
              </Dropdown.Button>
              <Dropdown.Content>
                <Menu onClick={handleExportKpiWRsWithoutTaskPDF} disabled={isExportingKpiWRsWithoutTaskPDF}>
                  {isExportingKpiWRsWithoutTaskPDF ? "Exporting..." : "PDF"}
                </Menu>
                <Menu onClick={handleExportKpiWRsWithoutTaskXLS} disabled={isExportingKpiWRsWithoutTaskXLS}>
                  {isExportingKpiWRsWithoutTaskXLS ? "Exporting..." : "XLS"}
                </Menu>
              </Dropdown.Content>
            </Dropdown>
          </div>
          <div className="px-5 pb-4 flex flex-col flex-1">
            <p style={{ fontSize: 32, fontWeight: 700, color: "#111827", margin: "4px 0" }}>{kpiData?.wrWithoutTask ?? "—"}</p>
            <p style={{ fontSize: 12, color: "#9ca3af", marginTop: "auto" }}>work requests need tasks assigned</p>
          </div>
        </div>

        {/* KPI 2: Tasks Without Assignment */}
        <div className="bg-white rounded-lg flex flex-col" style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
          <div className="flex items-start justify-between gap-2 px-5 pt-4 pb-2">
            <p style={{ fontSize: 13, fontWeight: 500, color: "#6b7280", lineHeight: 1.3 }}>Tasks Without Assignment</p>
            <Dropdown position="end">
              <Dropdown.Button>
                <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff", whiteSpace: "nowrap" }}>
                  Export
                </button>
              </Dropdown.Button>
              <Dropdown.Content>
                <Menu onClick={handleExportKpiTasksWithoutAssignmentPDF} disabled={isExportingKpiTasksWithoutAssignmentPDF}>
                  {isExportingKpiTasksWithoutAssignmentPDF ? "Exporting..." : "PDF"}
                </Menu>
                <Menu onClick={handleExportKpiTasksWithoutAssignmentXLS} disabled={isExportingKpiTasksWithoutAssignmentXLS}>
                  {isExportingKpiTasksWithoutAssignmentXLS ? "Exporting..." : "XLS"}
                </Menu>
              </Dropdown.Content>
            </Dropdown>
          </div>
          <div className="px-5 pb-4 flex flex-col flex-1">
            <p style={{ fontSize: 32, fontWeight: 700, color: "#111827", margin: "4px 0" }}>{kpiData?.tasksWithoutAssignment ?? "—"}</p>
            <p style={{ fontSize: 12, color: "#9ca3af", marginTop: "auto" }}>tasks need technician assigned</p>
          </div>
        </div>

        {/* KPI 3: Tasks Without Schedule */}
        <div className="bg-white rounded-lg flex flex-col" style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
          <div className="flex items-start justify-between gap-2 px-5 pt-4 pb-2">
            <p style={{ fontSize: 13, fontWeight: 500, color: "#6b7280", lineHeight: 1.3 }}>Tasks Without Schedule</p>
            <Dropdown position="end">
              <Dropdown.Button>
                <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff", whiteSpace: "nowrap" }}>
                  Export
                </button>
              </Dropdown.Button>
              <Dropdown.Content>
                <Menu onClick={handleExportKpiTasksWithoutSchedulePDF} disabled={isExportingKpiTasksWithoutSchedulePDF}>
                  {isExportingKpiTasksWithoutSchedulePDF ? "Exporting..." : "PDF"}
                </Menu>
                <Menu onClick={handleExportKpiTasksWithoutScheduleXLS} disabled={isExportingKpiTasksWithoutScheduleXLS}>
                  {isExportingKpiTasksWithoutScheduleXLS ? "Exporting..." : "XLS"}
                </Menu>
              </Dropdown.Content>
            </Dropdown>
          </div>
          <div className="px-5 pb-4 flex flex-col flex-1">
            <p style={{ fontSize: 32, fontWeight: 700, color: "#111827", margin: "4px 0" }}>{kpiData?.tasksWithoutSchedule ?? "—"}</p>
            <p style={{ fontSize: 12, color: "#9ca3af", marginTop: "auto" }}>tasks need scheduling</p>
          </div>
        </div>

        {/* KPI 4: Tasks Not Completed */}
        <div className="bg-white rounded-lg flex flex-col" style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
          <div className="flex items-start justify-between gap-2 px-5 pt-4 pb-2">
            <p style={{ fontSize: 13, fontWeight: 500, color: "#6b7280", lineHeight: 1.3 }}>Tasks Not Completed</p>
            <Dropdown position="end">
              <Dropdown.Button>
                <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff", whiteSpace: "nowrap" }}>
                  Export
                </button>
              </Dropdown.Button>
              <Dropdown.Content>
                <Menu onClick={handleExportKpiTasksNotCompletedPDF} disabled={isExportingKpiTasksNotCompletedPDF}>
                  {isExportingKpiTasksNotCompletedPDF ? "Exporting..." : "PDF"}
                </Menu>
                <Menu onClick={handleExportKpiTasksNotCompletedXLS} disabled={isExportingKpiTasksNotCompletedXLS}>
                  {isExportingKpiTasksNotCompletedXLS ? "Exporting..." : "XLS"}
                </Menu>
              </Dropdown.Content>
            </Dropdown>
          </div>
          <div className="px-5 pb-4 flex flex-col flex-1">
            <p style={{ fontSize: 32, fontWeight: 700, color: "#111827", margin: "4px 0" }}>{kpiData?.tasksNotCompleted ?? "—"}</p>
            <p style={{ fontSize: 12, color: "#9ca3af", marginTop: "auto" }}>tasks past due or stalled</p>
          </div>
        </div>
      </div>

      {/* 3-graph row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {/* Graph 1: Work Requests by Priority */}
        <ChartCard
          title="Work Requests by Priority"
          subtitle={`All open WRs by priority level — ${wrPeriodLabels[timePeriod]}`}
          rightHeader={
            <Dropdown position="end">
              <Dropdown.Button>
                <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                  Export
                </button>
              </Dropdown.Button>
              <Dropdown.Content>
                <Menu onClick={handleExportWRPriorityPDF} disabled={isExportingWRPriorityPDF}>
                  {isExportingWRPriorityPDF ? "Exporting PDF..." : "PDF"}
                </Menu>
                <Menu onClick={handleExportWRPriorityXLS} disabled={isExportingWRPriorityXLS}>
                  {isExportingWRPriorityXLS ? "Exporting XLS..." : "Excel"}
                </Menu>
              </Dropdown.Content>
            </Dropdown>
          }
        >
          <div className="flex flex-col">
            <div className="relative">
              <ResponsiveContainer width="100%" height={280}>
                <RadialBarChart
                  innerRadius={30}
                  outerRadius={110}
                  data={wrByPriority}
                  startAngle={180}
                  endAngle={-180}
                >
                  <RadialBar dataKey="value" background={{ fill: "#f3f4f6" }}>
                    {wrByPriority.map((entry: any, index: number) => (
                      <Cell key={index} fill={entry.fill} />
                    ))}
                  </RadialBar>
                  <Tooltip
                    content={({ active, payload }: any) => {
                      if (!active || !payload?.length) return null;
                      const item = payload[0].payload;
                      return (
                        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6, padding: "6px 10px", fontSize: 12 }}>
                          <p style={{ margin: 0, fontWeight: 600, color: item.fill }}>{item.name}</p>
                          <p style={{ margin: 0, color: "#374151" }}>{item.value} WRs</p>
                        </div>
                      );
                    }}
                  />
                </RadialBarChart>
              </ResponsiveContainer>
              <div
                className="absolute flex flex-col items-center"
                style={{ top: "50%", left: "50%", transform: "translate(-50%, -50%)", pointerEvents: "none" }}
              >
                <span style={{ fontSize: 24, fontWeight: 700, color: "#111827" }}>{totalOpenWRs}</span>
                <span style={{ fontSize: 11, color: "#6b7280" }}>Open WRs</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 justify-center pt-2">
              {[...wrByPriority].reverse().map((item: any) => (
                <div key={item.name} className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: item.fill }} />
                  <span style={{ fontSize: 12, color: "#6b7280" }}>{item.name} {item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </ChartCard>

        {/* Graph 2: Work Requests Without Any Task by Priority */}
        <ChartCard
          title="WRs Without Task by Priority"
          subtitle={`Work requests that have no task created, broken down by priority — ${wrPeriodLabels[timePeriod]}`}
          rightHeader={
            <Dropdown position="end">
              <Dropdown.Button>
                <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                  Export
                </button>
              </Dropdown.Button>
              <Dropdown.Content>
                <Menu onClick={handleExportWRsWithoutTaskByPriorityPDF} disabled={isExportingWRsWithoutTaskByPriorityPDF}>
                  {isExportingWRsWithoutTaskByPriorityPDF ? "Exporting..." : "PDF"}
                </Menu>
                <Menu onClick={handleExportWRsWithoutTaskByPriorityXLS} disabled={isExportingWRsWithoutTaskByPriorityXLS}>
                  {isExportingWRsWithoutTaskByPriorityXLS ? "Exporting..." : "XLS"}
                </Menu>
              </Dropdown.Content>
            </Dropdown>
          }
        >
          <div className="flex flex-col">
            <div className="relative">
              <ResponsiveContainer width="100%" height={280}>
                <RadialBarChart
                  innerRadius={30}
                  outerRadius={110}
                  data={wrsWithoutTaskChartData}
                  startAngle={180}
                  endAngle={-180}
                >
                  <RadialBar dataKey="value" background={{ fill: "#f3f4f6" }}>
                    {wrsWithoutTaskChartData.map((entry: any, index: number) => (
                      <Cell key={index} fill={entry.fill} />
                    ))}
                  </RadialBar>
                  <Tooltip
                    content={({ active, payload }: any) => {
                      if (!active || !payload?.length) return null;
                      const item = payload[0].payload;
                      return (
                        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6, padding: "6px 10px", fontSize: 12 }}>
                          <p style={{ margin: 0, fontWeight: 600, color: item.fill }}>{item.name}</p>
                          <p style={{ margin: 0, color: "#374151" }}>{item.value} WRs</p>
                        </div>
                      );
                    }}
                  />
                </RadialBarChart>
              </ResponsiveContainer>
              <div
                className="absolute flex flex-col items-center"
                style={{ top: "50%", left: "50%", transform: "translate(-50%, -50%)", pointerEvents: "none" }}
              >
                <span style={{ fontSize: 24, fontWeight: 700, color: "#111827" }}>{totalWRsWithoutTask}</span>
                <span style={{ fontSize: 11, color: "#6b7280" }}>WRs Without Task</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 justify-center pt-2">
              {[...wrsWithoutTaskChartData].reverse().map((item: any) => (
                <div key={item.name} className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: item.fill }} />
                  <span style={{ fontSize: 12, color: "#6b7280" }}>{item.name} {item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </ChartCard>

        {/* Graph 3: Task Funnel */}
        <ChartCard
          title="Task Funnel — Work Order Pipeline"
          subtitle={`Cumulative task pipeline from creation to completion — ${wrPeriodLabels[timePeriod]}`}
          rightHeader={
            <Dropdown position="end">
              <Dropdown.Button>
                <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                  Export
                </button>
              </Dropdown.Button>
              <Dropdown.Content>
                <Menu onClick={handleExportFunnelPDF} disabled={isExportingFunnelPDF}>
                  {isExportingFunnelPDF ? "Exporting PDF..." : "PDF"}
                </Menu>
                <Menu onClick={handleExportFunnelXLS} disabled={isExportingFunnelXLS}>
                  {isExportingFunnelXLS ? "Exporting XLS..." : "Excel"}
                </Menu>
              </Dropdown.Content>
            </Dropdown>
          }
        >
          <div className="flex flex-col">
            <ResponsiveContainer width="100%" height={320}>
              <FunnelChart>
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb" }}
                  formatter={(value: any, name: any) => [value, name]}
                />
                <Funnel dataKey="value" data={taskFunnelData} isAnimationActive>
                  {taskFunnelData.map((entry: any, index: number) => (
                    <Cell key={index} fill={entry.fill} />
                  ))}
                  <LabelList content={renderFunnelLabel} />
                </Funnel>
              </FunnelChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-x-4 gap-y-1 justify-center pt-2">
              {taskFunnelData.map((stage: any) => (
                <div key={stage.name} className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: stage.fill }} />
                  <span className="text-[11px] text-[#6b7280]">{stage.name}: {stage.value}</span>
                </div>
              ))}
            </div>
          </div>
        </ChartCard>
      </div>

      {/* Tasks Aging Table */}
      <ChartCard
        title="Tasks Aging with Priority"
        subtitle={`All open tasks by priority and age — ${wrPeriodLabels[timePeriod]}, excluding Closed and Cancelled`}
        rightHeader={
          <Dropdown position="end">
            <Dropdown.Button>
              <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                Export
              </button>
            </Dropdown.Button>
            <Dropdown.Content>
              <Menu onClick={handleExportAgingPDF} disabled={isExportingAgingPDF}>
                {isExportingAgingPDF ? "Exporting PDF..." : "PDF"}
              </Menu>
              <Menu onClick={handleExportAgingXLS} disabled={isExportingAgingXLS}>
                {isExportingAgingXLS ? "Exporting XLS..." : "Excel"}
              </Menu>
            </Dropdown.Content>
          </Dropdown>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th
                  style={{ background: "#f9fafb", fontSize: 12, fontWeight: 600, color: "#6b7280", textAlign: "center", padding: 10, width: 100 }}
                >
                  Priority
                </th>
                {agingCols.map((col) => (
                  <th
                    key={col}
                    style={{ background: "#f9fafb", fontSize: 12, fontWeight: 600, color: "#6b7280", textAlign: "center", padding: 10 }}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {agingRows.map((row: any, rowIdx: number) => {
                const badge = priorityBadgeConfig[row.priorityName] || { bg: "#f3f4f6", text: "#6b7280" };
                const vals = [row.day1, row.day2, row.day3, row.day4to7, row.day7to10, row.day10to15, row.day15Plus, row.total];
                return (
                  <tr key={row.priorityName} style={{ background: rowIdx % 2 === 1 ? "#fafafa" : "transparent" }}>
                    <td style={{ textAlign: "center", padding: 10 }}>
                      <span
                        style={{
                          background: badge.bg,
                          color: badge.text,
                          borderRadius: 4,
                          padding: "2px 8px",
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {row.priorityName}
                      </span>
                    </td>
                    {vals.map((v: number, i: number) => (
                      <td key={i} style={{ textAlign: "center", padding: 10, fontSize: 14 }}>
                        {v}
                      </td>
                    ))}
                  </tr>
                );
              })}
              <tr style={{ background: "#f9fafb", fontWeight: 700 }}>
                <td style={{ textAlign: "center", padding: 10, fontSize: 14 }}>Total</td>
                {agingTotalsCalc.map((v: number, i: number) => (
                  <td key={i} style={{ textAlign: "center", padding: 10, fontSize: 14 }}>
                    {v}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </ChartCard>

      {/* Remaining charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <ChartCard
          title="New High Priority Work Requests"
          subtitle={`Unscheduled high priority WRs — ${wrPeriodLabels[timePeriod]}`}
          className="h-full"
          rightHeader={
            <Dropdown position="end">
              <Dropdown.Button>
                <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                  Export
                </button>
              </Dropdown.Button>
              <Dropdown.Content>
                <Menu onClick={handleExportHighPriWRPDF} disabled={isExportingHighPriWRPDF}>
                  {isExportingHighPriWRPDF ? "Exporting PDF..." : "PDF"}
                </Menu>
                <Menu onClick={handleExportHighPriWRXLS} disabled={isExportingHighPriWRXLS}>
                  {isExportingHighPriWRXLS ? "Exporting XLS..." : "Excel"}
                </Menu>
              </Dropdown.Content>
            </Dropdown>
          }
        >
          {highPriWRs.length === 0 ? (
            <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "20px 0" }}>No high priority work requests found</p>
          ) : highPriWRs.map((wr: any, i: number) => {
            const badge = priorityBadgeConfig[wr.priorityName] || { bg: "#f3f4f6", text: "#6b7280" };
            const facilityDisplay = [wr.facilityName, wr.subFacilityName].filter(Boolean).join(" / ");
            return (
              <div
                key={wr.workRequestID || i}
                className="flex items-center justify-between gap-3"
                style={{ padding: "12px 0", borderBottom: i < highPriWRs.length - 1 ? "1px solid #f3f4f6" : "none" }}
              >
                <div className="flex flex-col gap-0.5 min-w-0">
                  <p style={{ fontWeight: 600, fontSize: 14, color: "#111827" }}>{wr.description}</p>
                  <p style={{ fontSize: 12, color: "#6b7280" }}>{facilityDisplay}</p>
                  <p style={{ fontSize: 12, color: "#6b7280" }}>Requester: {wr.requesterName}</p>
                  {wr.addedDate && <p style={{ fontSize: 11, color: "#9ca3af" }}>{new Date(wr.addedDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</p>}
                </div>
                <span
                  className="shrink-0"
                  style={{
                    background: badge.bg,
                    color: badge.text,
                    borderRadius: 4,
                    padding: "2px 8px",
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  {wr.priorityName}
                </span>
              </div>
            );
          })}
        </ChartCard>

        <div className="flex flex-col gap-5">
          <ChartCard
            title="Task Creation Time"
            subtitle={`Hours from WR creation to Task creation — ${wrPeriodLabels[timePeriod]}`}
            rightHeader={
              <Dropdown position="end">
                <Dropdown.Button>
                  <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                    Export
                  </button>
                </Dropdown.Button>
                <Dropdown.Content>
                  <Menu onClick={handleExportCreationTimePDF} disabled={isExportingCreationTimePDF}>
                    {isExportingCreationTimePDF ? "Exporting PDF..." : "PDF"}
                  </Menu>
                  <Menu onClick={handleExportCreationTimeXLS} disabled={isExportingCreationTimeXLS}>
                    {isExportingCreationTimeXLS ? "Exporting XLS..." : "Excel"}
                  </Menu>
                </Dropdown.Content>
              </Dropdown>
            }
          >
            <div className="flex flex-col">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={creationTimeData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: "#6b7280" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} label={{ value: "Tasks", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "#6b7280" }, offset: 15 }} />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb" }}
                    formatter={(value: any, name: any, props: any) => [`${value} tasks`, props.payload.bucket]}
                    labelFormatter={() => ""}
                  />
                  <Bar dataKey="count" fill="#2563eb" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <p style={{ fontSize: 12, color: "#6b7280", marginTop: 8 }}>Most common: {creationMedian}</p>
            </div>
          </ChartCard>

          <ChartCard
            title="Task Completion Time"
            subtitle={`Hours from Task creation to Closed-Complete — ${wrPeriodLabels[timePeriod]}`}
            rightHeader={
              <Dropdown position="end">
                <Dropdown.Button>
                  <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                    Export
                  </button>
                </Dropdown.Button>
                <Dropdown.Content>
                  <Menu onClick={handleExportCompletionTimePDF} disabled={isExportingCompletionTimePDF}>
                    {isExportingCompletionTimePDF ? "Exporting PDF..." : "PDF"}
                  </Menu>
                  <Menu onClick={handleExportCompletionTimeXLS} disabled={isExportingCompletionTimeXLS}>
                    {isExportingCompletionTimeXLS ? "Exporting XLS..." : "Excel"}
                  </Menu>
                </Dropdown.Content>
              </Dropdown>
            }
          >
            <div className="flex flex-col">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={completionTimeData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: "#6b7280" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} label={{ value: "Tasks", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "#6b7280" }, offset: 15 }} />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb" }}
                    formatter={(value: any, name: any, props: any) => [`${value} tasks`, props.payload.bucket]}
                    labelFormatter={() => ""}
                  />
                  <Bar dataKey="count" fill="#16a34a" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <p style={{ fontSize: 12, color: "#6b7280", marginTop: 8 }}>Most common: {completionMedian}</p>
            </div>
          </ChartCard>
        </div>
      </div>

      {/* Total Response Time */}
      <ChartCard
        title="Total Response Time"
        subtitle={`Hours from Work Request creation to completion — ${wrPeriodLabels[timePeriod]}`}
        rightHeader={
          <Dropdown position="end">
            <Dropdown.Button>
              <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                Export
              </button>
            </Dropdown.Button>
            <Dropdown.Content>
              <Menu onClick={handleExportTotalResponsePDF} disabled={isExportingTotalResponsePDF}>
                {isExportingTotalResponsePDF ? "Exporting PDF..." : "PDF"}
              </Menu>
              <Menu onClick={handleExportTotalResponseXLS} disabled={isExportingTotalResponseXLS}>
                {isExportingTotalResponseXLS ? "Exporting XLS..." : "Excel"}
              </Menu>
            </Dropdown.Content>
          </Dropdown>
        }
      >
        <div className="flex flex-col">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={totalResponseData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }} barCategoryGap="20%" barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: "#6b7280" }} />
              <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} label={{ value: "WRs", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "#6b7280" }, offset: 15 }} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb" }}
                formatter={(value: any, name: any) => [`${value} WRs`, name]}
              />
              <Legend iconType="square" iconSize={10} wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              {wrPriorityOrder.map((priority: string) => (
                <Bar key={priority} dataKey={priority} fill={wrPriorityColors[priority]} radius={[3, 3, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
          <p style={{ fontSize: 12, color: "#6b7280", marginTop: 8 }}>Most common bucket: {responseMedian}</p>
        </div>
      </ChartCard>

      {/* Tasks & Work Requests by Priority */}
      {(() => {
        const twrPriorityOrder = ["Immediate mortality risk", "Immediate flock performance impact", "Ok to fix between flocks", "Preventative"];
        const priorityColors: Record<string, string> = {
          "Immediate mortality risk":           "#dc2626",
          "Immediate flock performance impact": "#ea580c",
          "Ok to fix between flocks":           "#ca8a04",
          "Preventative":                       "#2563eb",
        };
        const priorityShortLabels: Record<string, string> = {
          "Immediate mortality risk":           "Mortality Risk",
          "Immediate flock performance impact": "Flock Performance",
          "Ok to fix between flocks":           "Between Flocks",
          "Preventative":                       "Preventative",
        };

        // Collect all facilities from both datasets
        const allFacilities = Array.from(new Set([
          ...tasksWRsFacilityTasks.map((r: any) => r.facilityName),
          ...tasksWRsFacilityWRs.map((r: any) => r.facilityName),
        ])).sort() as string[];

        // Build per-facility rows: { facility, T_<priority>, ..., W_<priority>, ... }
        const twrChartData = allFacilities.map(f => {
          const row: any = { facility: f };
          twrPriorityOrder.forEach(p => { row[`T_${p}`] = 0; row[`W_${p}`] = 0; });
          return row;
        });
        const facilityIndex: Record<string, number> = {};
        allFacilities.forEach((f, i) => { facilityIndex[f] = i; });

        tasksWRsFacilityTasks.forEach((r: any) => {
          const idx = facilityIndex[r.facilityName];
          if (idx !== undefined) twrChartData[idx][`T_${r.priorityName}`] = (twrChartData[idx][`T_${r.priorityName}`] || 0) + r.count;
        });
        tasksWRsFacilityWRs.forEach((r: any) => {
          const idx = facilityIndex[r.facilityName];
          if (idx !== undefined) twrChartData[idx][`W_${r.priorityName}`] = (twrChartData[idx][`W_${r.priorityName}`] || 0) + r.count;
        });

        const hasData = twrChartData.some(row => twrPriorityOrder.some(p => row[`T_${p}`] > 0 || row[`W_${p}`] > 0));


        return (
          <ChartCard
            title="Tasks & Work Requests by Priority"
            subtitle={`Tasks and WRs by facility, stacked by priority — ${wrPeriodLabels[timePeriod]}`}
            rightHeader={
              <Dropdown position="end">
                <Dropdown.Button>
                  <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                    Export
                  </button>
                </Dropdown.Button>
                <Dropdown.Content>
                  <Menu onClick={handleExportTasksWRsPDF} disabled={isExportingTasksWRsPDF}>
                    {isExportingTasksWRsPDF ? "Exporting PDF..." : "PDF"}
                  </Menu>
                  <Menu onClick={handleExportTasksWRsXLS} disabled={isExportingTasksWRsXLS}>
                    {isExportingTasksWRsXLS ? "Exporting XLS..." : "Excel"}
                  </Menu>
                </Dropdown.Content>
              </Dropdown>
            }
          >
            {!hasData ? (
              <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "20px 0" }}>No data found</p>
            ) : (
              <div>
                <ResponsiveContainer width="100%" height={Math.max(260, allFacilities.length * 42 + 60)}>
                  <BarChart data={twrChartData} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: "#6b7280" }} allowDecimals={false} />
                    <YAxis
                      type="category"
                      dataKey="facility"
                      tick={{ fontSize: 10, fill: "#374151" }}
                      width={130}
                      interval={0}
                    />
                    <Tooltip
                      contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb" }}
                      formatter={(value: any, name: any) => {
                        if (!value) return null;
                        const isTask = (name as string).startsWith("T_");
                        const priority = (name as string).replace(/^[TW]_/, "");
                        return [`${value}`, `${isTask ? "Tasks" : "WRs"} — ${priorityShortLabels[priority] ?? priority}`];
                      }}
                    />
                    {twrPriorityOrder.map((priority, i) => {
                      const isLast = i === twrPriorityOrder.length - 1;
                      const color = priorityColors[priority];
                      return [
                        <Bar key={`T_${priority}`} dataKey={`T_${priority}`} stackId="tasks"
                          fill={color} name={`T_${priority}`} legendType="none"
                          radius={isLast ? [0, 4, 4, 0] : [0, 0, 0, 0]} />,
                        <Bar key={`W_${priority}`} dataKey={`W_${priority}`} stackId="wrs"
                          fill={color} fillOpacity={0.45} name={`W_${priority}`} legendType="none"
                          radius={isLast ? [0, 4, 4, 0] : [0, 0, 0, 0]} />,
                      ];
                    })}
                  </BarChart>
                </ResponsiveContainer>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center", paddingTop: 8 }}>
                  {twrPriorityOrder.map(p => (
                    <span key={p} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#374151" }}>
                      <span style={{ display: "inline-block", width: 10, height: 10, background: priorityColors[p], borderRadius: 2, flexShrink: 0 }} />
                      {p}
                    </span>
                  ))}
                  <span style={{ fontSize: 11, color: "#9ca3af" }}>Solid = Tasks &nbsp;|&nbsp; Faded = Work Requests</span>
                </div>
              </div>
            )}
          </ChartCard>
        );
      })()}

      {/* Scheduled Tasks by Farm */}
      {(() => {
        const schedPriorityOrder = ["Immediate mortality risk", "Immediate flock performance impact", "Ok to fix between flocks", "Preventative"];
        const schedPriorityColors: Record<string, string> = {
          "Immediate mortality risk": "#dc2626",
          "Immediate flock performance impact": "#ea580c",
          "Ok to fix between flocks": "#ca8a04",
          "Preventative": "#2563eb",
        };
        const facilityMap: Record<string, Record<string, number>> = {};
        scheduledTasksData.forEach((r: any) => {
          if (!facilityMap[r.facilityName]) facilityMap[r.facilityName] = {};
          facilityMap[r.facilityName][r.priorityName] = r.taskCount;
        });
        const schedChartData = Object.entries(facilityMap).map(([facility, counts]) => ({
          facility,
          ...counts,
        }));
        const activePriorities = schedPriorityOrder.filter(p =>
          schedChartData.some((row: any) => (row[p] || 0) > 0)
        );
        const schedPeriodLabel: Record<number, string> = { 1: "Today", 3: "Next 3 Days", 7: "Next 7 Days", 30: "Next 30 Days" };
        const chartHeight = Math.max(200, schedChartData.length * 44);

        return (
          <ChartCard
            title="Scheduled Tasks by Farm"
            subtitle={`Open tasks with start date — ${schedPeriodLabel[schedPeriod]}`}
            rightHeader={
              <div className="flex gap-1 items-center">
                {([1, 3, 7, 30] as number[]).map((p) => (
                  <button
                    key={p}
                    onClick={() => setSchedPeriod(p)}
                    style={{
                      fontSize: 11,
                      padding: "3px 8px",
                      borderRadius: 4,
                      border: `1px solid ${schedPeriod === p ? "#16a34a" : "#d1d5db"}`,
                      background: schedPeriod === p ? "#16a34a" : "#fff",
                      color: schedPeriod === p ? "#fff" : "#6b7280",
                      cursor: "pointer",
                      fontWeight: schedPeriod === p ? 600 : 400,
                    }}
                  >
                    {schedPeriodLabel[p]}
                  </button>
                ))}
                <Dropdown position="end">
                  <Dropdown.Button>
                    <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>Export</button>
                  </Dropdown.Button>
                  <Dropdown.Content>
                    <Menu onClick={handleExportSchedPDF} disabled={isExportingSchedPDF}>
                      {isExportingSchedPDF ? "Exporting PDF..." : "PDF"}
                    </Menu>
                    <Menu onClick={handleExportSchedXLS} disabled={isExportingSchedXLS}>
                      {isExportingSchedXLS ? "Exporting XLS..." : "Excel"}
                    </Menu>
                  </Dropdown.Content>
                </Dropdown>
              </div>
            }
          >
            {schedChartData.length === 0 ? (
              <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "20px 0" }}>
                No scheduled tasks found for this period
              </p>
            ) : (
              <div>
                <ResponsiveContainer width="100%" height={chartHeight}>
                  <BarChart layout="vertical" data={schedChartData} margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: "#6b7280" }} allowDecimals={false} />
                    <YAxis
                      type="category"
                      dataKey="facility"
                      tick={{ fontSize: 11, fill: "#374151" }}
                      width={130}
                    />
                    <Tooltip
                      contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb" }}
                      formatter={(value: any, name: any) => [`${value} tasks`, name]}
                    />
                    <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                    {schedPriorityOrder.map((priority) => (
                      <Bar
                        key={priority}
                        dataKey={priority}
                        stackId="s"
                        fill={schedPriorityColors[priority]}
                        radius={priority === activePriorities[activePriorities.length - 1] ? [0, 4, 4, 0] : [0, 0, 0, 0]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </ChartCard>
        );
      })()}
    </div>
  );
}

const rmUsers = [
  { key: "Jake R.", color: "#16a34a" },
  { key: "Maria S.", color: "#2563eb" },
  { key: "Tom B.", color: "#d97706" },
  { key: "Carlos M.", color: "#7c3aed" },
  { key: "Anna K.", color: "#dc2626" },
  { key: "Dave L.", color: "#0891b2" },
];

const rmBase = [
  { facility: "Seahawk", "Jake R.": 12, "Maria S.": 8, "Tom B.": 10, "Carlos M.": 14, "Anna K.": 6, "Dave L.": 9 },
  { facility: "Bugeater", "Jake R.": 9, "Maria S.": 11, "Tom B.": 7, "Carlos M.": 8, "Anna K.": 10, "Dave L.": 6 },
  { facility: "Y24", "Jake R.": 6, "Maria S.": 5, "Tom B.": 8, "Carlos M.": 7, "Anna K.": 4, "Dave L.": 11 },
  { facility: "Bobcat", "Jake R.": 14, "Maria S.": 9, "Tom B.": 12, "Carlos M.": 10, "Anna K.": 8, "Dave L.": 7 },
  { facility: "Wolfpack", "Jake R.": 8, "Maria S.": 7, "Tom B.": 6, "Carlos M.": 9, "Anna K.": 5, "Dave L.": 8 },
  { facility: "Tar Heel", "Jake R.": 10, "Maria S.": 12, "Tom B.": 9, "Carlos M.": 11, "Anna K.": 7, "Dave L.": 10 },
];

function TimeOnFarmChart({ title, subtitle, users, baseData, exportPDF, exportXLS, exportFilename }: {
  title: string; subtitle: string; users: { key: string; color: string }[];
  baseData: Record<string, any>[];
  exportPDF: (onSuccess: Function, onError: Function) => void;
  exportXLS: (onSuccess: Function, onError: Function) => void;
  exportFilename: string;
}) {
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isExportingXLS, setIsExportingXLS] = useState(false);

  const handleExportPDF = () => {
    setIsExportingPDF(true);
    exportPDF(
      (resp: any) => { downloadPDF(resp, exportFilename); setIsExportingPDF(false); },
      () => { setIsExportingPDF(false); }
    );
  };

  const handleExportXLS = () => {
    setIsExportingXLS(true);
    exportXLS(
      (resp: any) => { downloadExcel(resp, exportFilename); setIsExportingXLS(false); },
      () => { setIsExportingXLS(false); }
    );
  };

  return (
    <ChartCard
      title={title}
      subtitle={subtitle}
      rightHeader={
        <Dropdown position="end">
          <Dropdown.Button>
            <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
              Export
            </button>
          </Dropdown.Button>
          <Dropdown.Content>
            <Menu onClick={handleExportPDF} disabled={isExportingPDF}>
              {isExportingPDF ? "Exporting PDF..." : "PDF"}
            </Menu>
            <Menu onClick={handleExportXLS} disabled={isExportingXLS}>
              {isExportingXLS ? "Exporting XLS..." : "Excel"}
            </Menu>
          </Dropdown.Content>
        </Dropdown>
      }
    >
      <div className="flex flex-col">
        <ResponsiveContainer width="100%" height={Math.max(320, baseData.length * 44)}>
          <BarChart data={baseData} layout="vertical" margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11, fill: "#6b7280" }} label={{ value: "Hours", position: "insideBottom", offset: -2, style: { fontSize: 11, fill: "#6b7280" } }} />
            <YAxis type="category" dataKey="facility" tick={{ fontSize: 10, fill: "#6b7280" }} width={145} />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb" }}
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const total = payload.reduce((s, p) => s + (Number(p.value) || 0), 0);
                const sorted = [...payload].sort((a, b) => (Number(b.value) || 0) - (Number(a.value) || 0));
                return (
                  <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 12px", fontSize: 12 }}>
                    <p style={{ fontWeight: 600, marginBottom: 4 }}>{label}</p>
                    {sorted.map((entry) => (
                      <p key={entry.dataKey as string} style={{ color: entry.color, margin: "2px 0" }}>
                        {entry.dataKey}: {entry.value} hrs
                      </p>
                    ))}
                    <p style={{ fontWeight: 600, marginTop: 4, borderTop: "1px solid #e5e7eb", paddingTop: 4 }}>
                      Total: {Math.round(total * 10) / 10} hrs
                    </p>
                  </div>
                );
              }}
            />
            {users.map((u, i) => (
              <Bar key={u.key} dataKey={u.key} stackId="a" fill={u.color} radius={i === users.length - 1 ? [0, 3, 3, 0] : [0, 0, 0, 0]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
        <div className="flex flex-wrap gap-x-4 gap-y-1 justify-center pt-2">
          {users.map((u) => (
            <div key={u.key} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: u.color }} />
              <span style={{ fontSize: 12, color: "#6b7280" }}>{u.key}</span>
            </div>
          ))}
        </div>
      </div>
    </ChartCard>
  );
}

const completedTasksUsers = [
  { key: "Jake R.", color: "#16a34a", base: 28 },
  { key: "Maria S.", color: "#2563eb", base: 34 },
  { key: "Tom B.", color: "#d97706", base: 22 },
  { key: "Carlos M.", color: "#7c3aed", base: 41 },
  { key: "Anna K.", color: "#dc2626", base: 18 },
  { key: "Dave L.", color: "#0891b2", base: 26 },
  { key: "Mike T.", color: "#f59e0b", base: 31 },
  { key: "Sarah L.", color: "#10b981", base: 29 },
];

const loggedHoursData: Record<TimePeriod, { name: string; hours: number }[]> = {
  "7D": [
    { name: "Jake R.", hours: 4 }, { name: "Maria S.", hours: 6 }, { name: "Tom B.", hours: 3 },
    { name: "Carlos M.", hours: 7 }, { name: "Anna K.", hours: 2 }, { name: "Dave L.", hours: 5 },
  ],
  "30D": [
    { name: "Jake R.", hours: 18 }, { name: "Maria S.", hours: 24 }, { name: "Tom B.", hours: 14 },
    { name: "Carlos M.", hours: 28 }, { name: "Anna K.", hours: 11 }, { name: "Dave L.", hours: 20 },
  ],
  "120D": [
    { name: "Jake R.", hours: 72 }, { name: "Maria S.", hours: 96 }, { name: "Tom B.", hours: 58 },
    { name: "Carlos M.", hours: 110 }, { name: "Anna K.", hours: 44 }, { name: "Dave L.", hours: 81 },
  ],
  "12Mo": [
    { name: "Jake R.", hours: 280 }, { name: "Maria S.", hours: 370 }, { name: "Tom B.", hours: 220 },
    { name: "Carlos M.", hours: 430 }, { name: "Anna K.", hours: 170 }, { name: "Dave L.", hours: 310 },
  ],
};

function TimePeriodToggle({ period, setPeriod }: {
  period: TimePeriod; setPeriod: (p: TimePeriod) => void;
}) {
  return (
    <div className="flex gap-1">
      {(["7D", "30D", "120D", "12Mo"] as TimePeriod[]).map((p) => (
        <button
          key={p}
          onClick={() => setPeriod(p)}
          aria-pressed={period === p}
          style={{
            padding: "4px 10px", fontSize: 12, fontWeight: 500, borderRadius: 4, cursor: "pointer",
            background: period === p ? "#2B5A2E" : "white",
            color: period === p ? "white" : "#6b7280",
            border: period === p ? "1px solid #2B5A2E" : "1px solid #e5e7eb",
          }}
        >
          {p}
        </button>
      ))}
    </div>
  );
}

function CompletedTasksDonut() {
  const [period, setPeriod] = useState<TimePeriod>("30D");
  const multiplier = period === "7D" ? 0.25 : period === "120D" ? 4 : period === "12Mo" ? 12 : 1;
  const data = completedTasksUsers.map((u) => ({
    name: u.key, value: Math.round(u.base * multiplier), color: u.color,
  }));
  const total = data.reduce((s, d) => s + d.value, 0);

  return (
    <ChartCard
      title="Completed Tasks by Employee"
      subtitle="Closed-Complete tasks by user"
      rightHeader={<TimePeriodToggle period={period} setPeriod={setPeriod} />}
    >
      <div className="flex items-center justify-center gap-4">
        <div className="relative shrink-0" style={{ width: 240, height: 260 }}>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={100}>
                {data.map((d, i) => <Cell key={i} fill={d.color} stroke="white" strokeWidth={2} />)}
              </Pie>
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const p = payload[0];
                  const pct = total > 0 ? ((Number(p.value) / total) * 100).toFixed(1) : "0";
                  return (
                    <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 12px", fontSize: 12 }}>
                      <p style={{ fontWeight: 600 }}>{p.name}</p>
                      <p>{p.value} tasks ({pct}%)</p>
                    </div>
                  );
                }}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span style={{ fontSize: 22, fontWeight: 700, color: "#111827" }}>{total}</span>
            <span style={{ fontSize: 11, color: "#6b7280" }}>Tasks</span>
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          {data.map((d) => (
            <div key={d.name} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: d.color }} />
              <span style={{ fontSize: 12, color: "#6b7280" }}>{d.name} — {d.value}</span>
            </div>
          ))}
        </div>
      </div>
    </ChartCard>
  );
}

function LoggedHoursChart() {
  const [period, setPeriod] = useState<TimePeriod>("30D");
  const data = loggedHoursData[period];

  return (
    <ChartCard
      title="Logged Hours by Employee — In Progress"
      subtitle="Hours logged on tasks with status = In Progress"
      rightHeader={<TimePeriodToggle period={period} setPeriod={setPeriod} />}
    >
      <div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#6b7280" }} />
            <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} label={{ value: "Hours", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "#6b7280" }, offset: 15 }} />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb" }}
              formatter={(value: any) => [`${value} hrs`, "Hours logged"]}
            />
            <Bar dataKey="hours" fill="#2B5A2E" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
}

const pmReactiveCount = [
  { week: "Wk 1", PM: 8, Reactive: 22 }, { week: "Wk 2", PM: 10, Reactive: 19 },
  { week: "Wk 3", PM: 7, Reactive: 25 }, { week: "Wk 4", PM: 12, Reactive: 18 },
  { week: "Wk 5", PM: 9, Reactive: 21 }, { week: "Wk 6", PM: 11, Reactive: 20 },
  { week: "Wk 7", PM: 13, Reactive: 17 }, { week: "Wk 8", PM: 10, Reactive: 23 },
  { week: "Wk 9", PM: 14, Reactive: 16 }, { week: "Wk 10", PM: 11, Reactive: 19 },
  { week: "Wk 11", PM: 15, Reactive: 15 }, { week: "Wk 12", PM: 13, Reactive: 18 },
];

function PMReactiveChart() {
  const [metric, setMetric] = useState<"Count" | "Hours">("Count");
  const data = metric === "Count"
    ? pmReactiveCount
    : pmReactiveCount.map((r) => ({
      week: r.week,
      PM: Math.round(r.PM * 2.4 * 10) / 10,
      Reactive: Math.round(r.Reactive * 1.8 * 10) / 10,
    }));
  const unit = metric === "Count" ? "tasks" : "hrs";

  return (
    <ChartCard
      title="PM vs Reactive Tasks"
      subtitle="Preventive Maintenance tasks vs Work Request tasks — last 12 weeks"
      rightHeader={
        <div className="flex">
          {(["Count", "Hours"] as const).map((m) => (
            <button
              key={m}
              onClick={() => setMetric(m)}
              aria-pressed={metric === m}
              style={{
                padding: "4px 12px", fontSize: 12, fontWeight: 500, cursor: "pointer",
                background: metric === m ? "#2B5A2E" : "white",
                color: metric === m ? "white" : "#6b7280",
                border: metric === m ? "1px solid #2B5A2E" : "1px solid #e5e7eb",
                borderRadius: m === "Count" ? "4px 0 0 4px" : "0 4px 4px 0",
              }}
            >
              {m}
            </button>
          ))}
        </div>
      }
    >
      <div className="flex flex-col">
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
            <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#6b7280" }} />
            <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} label={{ value: metric === "Count" ? "Tasks" : "Hours", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "#6b7280" }, offset: 15 }} />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const pm = Number(payload.find((p) => p.dataKey === "PM")?.value || 0);
                const re = Number(payload.find((p) => p.dataKey === "Reactive")?.value || 0);
                return (
                  <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 12px", fontSize: 12 }}>
                    <p style={{ fontWeight: 600, marginBottom: 4 }}>{label}</p>
                    <p style={{ color: "#16a34a", margin: "2px 0" }}>PM: {pm} {unit}</p>
                    <p style={{ color: "#3b82f6", margin: "2px 0" }}>Reactive: {re} {unit}</p>
                    <p style={{ fontWeight: 600, marginTop: 4, borderTop: "1px solid #e5e7eb", paddingTop: 4 }}>
                      Total: {Math.round((pm + re) * 10) / 10} {unit}
                    </p>
                  </div>
                );
              }}
            />
            <Bar dataKey="PM" stackId="a" fill="#16a34a" />
            <Bar dataKey="Reactive" stackId="a" fill="#3b82f6" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
        <div className="flex flex-wrap gap-x-6 gap-y-1 justify-center pt-2">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: "#16a34a" }} />
            <span style={{ fontSize: 12, color: "#6b7280" }}>PM (Preventive Maintenance)</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: "#3b82f6" }} />
            <span style={{ fontSize: 12, color: "#6b7280" }}>Reactive (Work Request)</span>
          </div>
        </div>
      </div>
    </ChartCard>
  );
}

const DONUT_COLORS = ["#16a34a", "#2563eb", "#d97706", "#7c3aed", "#dc2626", "#0891b2", "#f59e0b", "#10b981", "#ec4899", "#6366f1", "#14b8a6", "#f43f5e"];

function transformTimeOnFarm(rows: any[]): Record<string, any>[] {
  const facilityMap = new Map<string, Record<string, any>>();
  rows.forEach((r) => {
    if (!facilityMap.has(r.facilityName)) facilityMap.set(r.facilityName, { facility: r.facilityName });
    facilityMap.get(r.facilityName)![r.userName] = Math.round(r.hours * 10) / 10;
  });
  return Array.from(facilityMap.values());
}

function extractTimeOnFarmUsers(rows: any[], colors: string[]): { key: string; color: string }[] {
  const seen = new Map<string, { key: string; color: string }>();
  rows.forEach((r) => {
    if (!seen.has(r.userName)) {
      seen.set(r.userName, { key: r.userName, color: colors[seen.size % colors.length] });
    }
  });
  return Array.from(seen.values());
}

const PM_MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const PM_DAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

function fmtPMDate(date: Date): string {
  return `${date.getDate()} ${PM_MONTHS[date.getMonth()]}`;
}

const PMXAxisTick = ({ x, y, payload }: any) => {
  const [line1, line2] = (payload.value ?? '').split("|");
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={10} textAnchor="middle" fill="#6b7280" fontSize={11}>{line1}</text>
      {line2 && <text x={0} y={0} dy={22} textAnchor="middle" fill="#9ca3af" fontSize={9}>{line2}</text>}
    </g>
  );
};

function buildPurchasingWeekLabels(count: number, days: number): string[] {
  const now = new Date();
  const startDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  const labels: string[] = [];
  if (days <= 7) {
    for (let i = 0; i < count; i++) {
      const d = new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000);
      labels.push(`${PM_DAYS[d.getDay()]}|${fmtPMDate(d)}`);
    }
  } else if (days <= 31) {
    for (let i = 0; i < count; i++) {
      const wStart = new Date(startDate.getTime() + i * 7 * 24 * 60 * 60 * 1000);
      const wEnd = new Date(startDate.getTime() + Math.min((i + 1) * 7 - 1, days - 1) * 24 * 60 * 60 * 1000);
      const rangeStr = wStart.getMonth() === wEnd.getMonth()
        ? `${wStart.getDate()}-${wEnd.getDate()} ${PM_MONTHS[wEnd.getMonth()]}`
        : `${wStart.getDate()} ${PM_MONTHS[wStart.getMonth()]}-${wEnd.getDate()} ${PM_MONTHS[wEnd.getMonth()]}`;
      labels.push(`Week ${i + 1}|${rangeStr}`);
    }
  } else {
    for (let i = 0; i < count; i++) {
      const d = new Date(startDate.getFullYear(), startDate.getMonth() + i, 1);
      labels.push(`${PM_MONTHS[d.getMonth()]}|${d.getFullYear()}`);
    }
  }
  return labels;
}

function buildPMReactiveDateLabels(count: number, days: number): string[] {
  const now = new Date();
  const startDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  const labels: string[] = [];
  if (days <= 7) {
    for (let i = 0; i < count; i++) {
      const d = new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000);
      labels.push(`${PM_DAYS[d.getDay()]}|${fmtPMDate(d)}`);
    }
  } else if (days <= 31) {
    for (let i = 0; i < count; i++) {
      const wStart = new Date(startDate.getTime() + i * 7 * 24 * 60 * 60 * 1000);
      const wEnd = new Date(startDate.getTime() + Math.min((i + 1) * 7 - 1, days - 1) * 24 * 60 * 60 * 1000);
      const rangeStr = wStart.getMonth() === wEnd.getMonth()
        ? `${wStart.getDate()}-${wEnd.getDate()} ${PM_MONTHS[wEnd.getMonth()]}`
        : `${wStart.getDate()} ${PM_MONTHS[wStart.getMonth()]}-${wEnd.getDate()} ${PM_MONTHS[wEnd.getMonth()]}`;
      labels.push(`Week ${i + 1}|${rangeStr}`);
    }
  } else {
    for (let i = 0; i < count; i++) {
      const d = new Date(startDate.getTime() + i * 30 * 24 * 60 * 60 * 1000);
      labels.push(`${PM_MONTHS[d.getMonth()]}|${d.getFullYear()}`);
    }
  }
  return labels;
}

function TeamMetricsTab({ days, timePeriod }: { days: number; timePeriod: WRTimePeriod }) {
  const dashboardApi = useDashboardApi();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [timeOnFarmLPRaw, setTimeOnFarmLPRaw] = useState<any[]>([]);
  const [timeOnFarmLPLoading, setTimeOnFarmLPLoading] = useState(true);
  const [timeOnFarmRMRaw, setTimeOnFarmRMRaw] = useState<any[]>([]);
  const [timeOnFarmRMLoading, setTimeOnFarmRMLoading] = useState(true);
  const [completedTasksByFarmRaw, setCompletedTasksByFarmRaw] = useState<any[]>([]);
  const [completedTasksByFarmLoading, setCompletedTasksByFarmLoading] = useState(true);
  const [isExportingTasksByFarmPDF, setIsExportingTasksByFarmPDF] = useState(false);
  const [isExportingTasksByFarmXLS, setIsExportingTasksByFarmXLS] = useState(false);
  const [completedTasksRMRaw, setCompletedTasksRMRaw] = useState<any[]>([]);
  const [completedTasksRMLoading, setCompletedTasksRMLoading] = useState(true);
  const [isExportingTasksRMPDF, setIsExportingTasksRMPDF] = useState(false);
  const [isExportingTasksRMXLS, setIsExportingTasksRMXLS] = useState(false);
  const [completedTasksLPRaw, setCompletedTasksLPRaw] = useState<any[]>([]);
  const [completedTasksLPLoading, setCompletedTasksLPLoading] = useState(true);
  const [isExportingTasksLPPDF, setIsExportingTasksLPPDF] = useState(false);
  const [isExportingTasksLPXLS, setIsExportingTasksLPXLS] = useState(false);
  const [completedTasksGrowerRaw, setCompletedTasksGrowerRaw] = useState<any[]>([]);
  const [completedTasksGrowerLoading, setCompletedTasksGrowerLoading] = useState(true);
  const [isExportingTasksGrowerPDF, setIsExportingTasksGrowerPDF] = useState(false);
  const [isExportingTasksGrowerXLS, setIsExportingTasksGrowerXLS] = useState(false);
  const [loggedHoursRMRaw, setLoggedHoursRMRaw] = useState<any[]>([]);
  const [loggedHoursRMLoading, setLoggedHoursRMLoading] = useState(true);
  const [isExportingLoggedHoursRMPDF, setIsExportingLoggedHoursRMPDF] = useState(false);
  const [isExportingLoggedHoursRMXLS, setIsExportingLoggedHoursRMXLS] = useState(false);
  const [loggedHoursLPRaw, setLoggedHoursLPRaw] = useState<any[]>([]);
  const [loggedHoursLPLoading, setLoggedHoursLPLoading] = useState(true);
  const [isExportingLoggedHoursLPPDF, setIsExportingLoggedHoursLPPDF] = useState(false);
  const [isExportingLoggedHoursLPXLS, setIsExportingLoggedHoursLPXLS] = useState(false);
  const [loggedHoursGrowerRaw, setLoggedHoursGrowerRaw] = useState<any[]>([]);
  const [loggedHoursGrowerLoading, setLoggedHoursGrowerLoading] = useState(true);
  const [isExportingLoggedHoursGrowerPDF, setIsExportingLoggedHoursGrowerPDF] = useState(false);
  const [isExportingLoggedHoursGrowerXLS, setIsExportingLoggedHoursGrowerXLS] = useState(false);
  const [isExportingPMReactivePDF, setIsExportingPMReactivePDF] = useState(false);
  const [isExportingPMReactiveXLS, setIsExportingPMReactiveXLS] = useState(false);
  const [avgHoursKpiData, setAvgHoursKpiData] = useState<any>(null);
  const [avgHoursKpiLoading, setAvgHoursKpiLoading] = useState(true);
  const [isExportingAvgHoursPDF, setIsExportingAvgHoursPDF] = useState(false);
  const [isExportingAvgHoursXLS, setIsExportingAvgHoursXLS] = useState(false);

  useEffect(() => {
    setLoading(true);
    dashboardApi.getManagerDashboardTeam(
      { Days: days },
      (response: any) => {
        const d = response?.data || response;
        setData(d);
        setLoading(false);
      },
      (error: any) => { console.error("Failed to fetch team metrics:", error); setLoading(false); }
    );
  }, [days]);

  useEffect(() => {
    setTimeOnFarmLPLoading(true);
    dashboardApi.getManagerHoursByFacilityLP(
      { Days: days },
      (response: any) => {
        const rows = response?.data || response || [];
        setTimeOnFarmLPRaw(Array.isArray(rows) ? rows : []);
        setTimeOnFarmLPLoading(false);
      },
      (error: any) => {
        console.error("Failed to fetch LP time on farm data:", error);
        setTimeOnFarmLPRaw([]);
        setTimeOnFarmLPLoading(false);
      }
    );
    setTimeOnFarmRMLoading(true);
    dashboardApi.getManagerHoursByFacilityRM(
      { Days: days },
      (response: any) => {
        const rows = response?.data || response || [];
        setTimeOnFarmRMRaw(Array.isArray(rows) ? rows : []);
        setTimeOnFarmRMLoading(false);
      },
      (error: any) => {
        console.error("Failed to fetch R&M time on farm data:", error);
        setTimeOnFarmRMRaw([]);
        setTimeOnFarmRMLoading(false);
      }
    );
  }, [days]);

  useEffect(() => {
    setCompletedTasksByFarmLoading(true);
    dashboardApi.getCompletedTasksByFarm(
      { Days: days },
      (response: any) => {
        const rows = response?.data || response || [];
        setCompletedTasksByFarmRaw(Array.isArray(rows) ? rows : []);
        setCompletedTasksByFarmLoading(false);
      },
      (error: any) => {
        console.error("Failed to fetch completed tasks by farm:", error);
        setCompletedTasksByFarmRaw([]);
        setCompletedTasksByFarmLoading(false);
      }
    );
    const fetchRole = (
      apiFn: Function, setRaw: Function, setLoading: Function, label: string
    ) => {
      setLoading(true);
      apiFn(
        { Days: days },
        (response: any) => { const rows = response?.data || response || []; setRaw(Array.isArray(rows) ? rows : []); setLoading(false); },
        (error: any) => { console.error(`Failed to fetch completed tasks ${label}:`, error); setRaw([]); setLoading(false); }
      );
    };
    fetchRole(dashboardApi.getCompletedTasksByRM, setCompletedTasksRMRaw, setCompletedTasksRMLoading, 'R&M');
    fetchRole(dashboardApi.getCompletedTasksByLP, setCompletedTasksLPRaw, setCompletedTasksLPLoading, 'LP');
    fetchRole(dashboardApi.getCompletedTasksByGrower, setCompletedTasksGrowerRaw, setCompletedTasksGrowerLoading, 'Grower');
    fetchRole(dashboardApi.getLoggedHoursByRM, setLoggedHoursRMRaw, setLoggedHoursRMLoading, 'LoggedRM');
    fetchRole(dashboardApi.getLoggedHoursByLP, setLoggedHoursLPRaw, setLoggedHoursLPLoading, 'LoggedLP');
    fetchRole(dashboardApi.getLoggedHoursByGrower, setLoggedHoursGrowerRaw, setLoggedHoursGrowerLoading, 'LoggedGrower');
  }, [days]);

  useEffect(() => {
    setAvgHoursKpiLoading(true);
    dashboardApi.getAdoptionDashboardCounts(
      { Days: days },
      (response: any) => {
        const d = response?.data || response;
        setAvgHoursKpiData(d || null);
        setAvgHoursKpiLoading(false);
      },
      () => { setAvgHoursKpiLoading(false); }
    );
  }, [days]);

  const handleExportTasksByFarmPDF = () => {
    setIsExportingTasksByFarmPDF(true);
    dashboardApi.exportCompletedTasksByFarmToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'completed_tasks_by_farm'); setIsExportingTasksByFarmPDF(false); },
      () => { setIsExportingTasksByFarmPDF(false); }
    );
  };

  const handleExportTasksByFarmXLS = () => {
    setIsExportingTasksByFarmXLS(true);
    dashboardApi.exportCompletedTasksByFarmToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'completed_tasks_by_farm'); setIsExportingTasksByFarmXLS(false); },
      () => { setIsExportingTasksByFarmXLS(false); }
    );
  };

  const handleExportTasksRMPDF = () => {
    setIsExportingTasksRMPDF(true);
    dashboardApi.exportCompletedTasksByRMToPDF({ Days: days }, (resp: any) => { downloadPDF(resp, 'completed_tasks_rm'); setIsExportingTasksRMPDF(false); }, () => { setIsExportingTasksRMPDF(false); });
  };
  const handleExportTasksRMXLS = () => {
    setIsExportingTasksRMXLS(true);
    dashboardApi.exportCompletedTasksByRMToXLS({ Days: days }, (resp: any) => { downloadExcel(resp, 'completed_tasks_rm'); setIsExportingTasksRMXLS(false); }, () => { setIsExportingTasksRMXLS(false); });
  };
  const handleExportTasksLPPDF = () => {
    setIsExportingTasksLPPDF(true);
    dashboardApi.exportCompletedTasksByLPToPDF({ Days: days }, (resp: any) => { downloadPDF(resp, 'completed_tasks_lp'); setIsExportingTasksLPPDF(false); }, () => { setIsExportingTasksLPPDF(false); });
  };
  const handleExportTasksLPXLS = () => {
    setIsExportingTasksLPXLS(true);
    dashboardApi.exportCompletedTasksByLPToXLS({ Days: days }, (resp: any) => { downloadExcel(resp, 'completed_tasks_lp'); setIsExportingTasksLPXLS(false); }, () => { setIsExportingTasksLPXLS(false); });
  };
  const handleExportTasksGrowerPDF = () => {
    setIsExportingTasksGrowerPDF(true);
    dashboardApi.exportCompletedTasksByGrowerToPDF({ Days: days }, (resp: any) => { downloadPDF(resp, 'completed_tasks_grower'); setIsExportingTasksGrowerPDF(false); }, () => { setIsExportingTasksGrowerPDF(false); });
  };
  const handleExportTasksGrowerXLS = () => {
    setIsExportingTasksGrowerXLS(true);
    dashboardApi.exportCompletedTasksByGrowerToXLS({ Days: days }, (resp: any) => { downloadExcel(resp, 'completed_tasks_grower'); setIsExportingTasksGrowerXLS(false); }, () => { setIsExportingTasksGrowerXLS(false); });
  };
  const handleExportLoggedHoursRMPDF = () => {
    setIsExportingLoggedHoursRMPDF(true);
    dashboardApi.exportLoggedHoursByRMToPDF({ Days: days }, (resp: any) => { downloadPDF(resp, 'logged_hours_rm'); setIsExportingLoggedHoursRMPDF(false); }, () => { setIsExportingLoggedHoursRMPDF(false); });
  };
  const handleExportLoggedHoursRMXLS = () => {
    setIsExportingLoggedHoursRMXLS(true);
    dashboardApi.exportLoggedHoursByRMToXLS({ Days: days }, (resp: any) => { downloadExcel(resp, 'logged_hours_rm'); setIsExportingLoggedHoursRMXLS(false); }, () => { setIsExportingLoggedHoursRMXLS(false); });
  };
  const handleExportLoggedHoursLPPDF = () => {
    setIsExportingLoggedHoursLPPDF(true);
    dashboardApi.exportLoggedHoursByLPToPDF({ Days: days }, (resp: any) => { downloadPDF(resp, 'logged_hours_lp'); setIsExportingLoggedHoursLPPDF(false); }, () => { setIsExportingLoggedHoursLPPDF(false); });
  };
  const handleExportLoggedHoursLPXLS = () => {
    setIsExportingLoggedHoursLPXLS(true);
    dashboardApi.exportLoggedHoursByLPToXLS({ Days: days }, (resp: any) => { downloadExcel(resp, 'logged_hours_lp'); setIsExportingLoggedHoursLPXLS(false); }, () => { setIsExportingLoggedHoursLPXLS(false); });
  };
  const handleExportLoggedHoursGrowerPDF = () => {
    setIsExportingLoggedHoursGrowerPDF(true);
    dashboardApi.exportLoggedHoursByGrowerToPDF({ Days: days }, (resp: any) => { downloadPDF(resp, 'logged_hours_grower'); setIsExportingLoggedHoursGrowerPDF(false); }, () => { setIsExportingLoggedHoursGrowerPDF(false); });
  };
  const handleExportLoggedHoursGrowerXLS = () => {
    setIsExportingLoggedHoursGrowerXLS(true);
    dashboardApi.exportLoggedHoursByGrowerToXLS({ Days: days }, (resp: any) => { downloadExcel(resp, 'logged_hours_grower'); setIsExportingLoggedHoursGrowerXLS(false); }, () => { setIsExportingLoggedHoursGrowerXLS(false); });
  };
  const handleExportPMReactivePDF = () => {
    setIsExportingPMReactivePDF(true);
    dashboardApi.exportPMReactiveToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'pm_vs_reactive'); setIsExportingPMReactivePDF(false); },
      () => { setIsExportingPMReactivePDF(false); }
    );
  };
  const handleExportPMReactiveXLS = () => {
    setIsExportingPMReactiveXLS(true);
    dashboardApi.exportPMReactiveToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'pm_vs_reactive'); setIsExportingPMReactiveXLS(false); },
      () => { setIsExportingPMReactiveXLS(false); }
    );
  };
  const handleExportAvgHoursPDF = () => {
    setIsExportingAvgHoursPDF(true);
    dashboardApi.exportKpiAvgHoursToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'avg_hours_per_task'); setIsExportingAvgHoursPDF(false); },
      () => { setIsExportingAvgHoursPDF(false); }
    );
  };
  const handleExportAvgHoursXLS = () => {
    setIsExportingAvgHoursXLS(true);
    dashboardApi.exportKpiAvgHoursToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'avg_hours_per_task'); setIsExportingAvgHoursXLS(false); },
      () => { setIsExportingAvgHoursXLS(false); }
    );
  };

  if (loading) return <WRTabLoader />;

  // Completed Tasks by Role
  const completedTasksRM = completedTasksRMRaw.map((r: any, i: number) => ({
    name: r.userName?.trim(), value: r.taskCount, color: DONUT_COLORS[i % DONUT_COLORS.length],
  }));
  const totalRM = completedTasksRM.reduce((s: number, r: any) => s + r.value, 0);

  const completedTasksLP = completedTasksLPRaw.map((r: any, i: number) => ({
    name: r.userName?.trim(), value: r.taskCount, color: DONUT_COLORS[i % DONUT_COLORS.length],
  }));
  const totalLP = completedTasksLP.reduce((s: number, r: any) => s + r.value, 0);

  const completedTasksGrower = completedTasksGrowerRaw.map((r: any, i: number) => ({
    name: r.userName?.trim(), value: r.taskCount, color: DONUT_COLORS[i % DONUT_COLORS.length],
  }));
  const totalGrower = completedTasksGrower.reduce((s: number, r: any) => s + r.value, 0);

  // Logged Hours by Role
  const loggedHoursRM = loggedHoursRMRaw.map((r: any) => ({ name: r.userName?.trim(), hours: Math.round((r.hours || 0) * 10) / 10 }));
  const loggedHoursLP = loggedHoursLPRaw.map((r: any) => ({ name: r.userName?.trim(), hours: Math.round((r.hours || 0) * 10) / 10 }));
  const loggedHoursGrower = loggedHoursGrowerRaw.map((r: any) => ({ name: r.userName?.trim(), hours: Math.round((r.hours || 0) * 10) / 10 }));

  // PM vs Reactive
  const pmReactiveRaw = (data?.pmReactive || []);
  const pmDateLabels = buildPMReactiveDateLabels(pmReactiveRaw.length, days);
  const pmReactiveData = pmReactiveRaw.map((r: any, i: number) => ({
    week: pmDateLabels[i] ?? r.bucketLabel, PM: r.pm || 0, Reactive: r.reactive || 0,
  }));

  // Completed Tasks by Farm
  const completedTasksByFarm = completedTasksByFarmRaw.map((r: any, i: number) => ({
    name: r.facilityName?.trim(), value: r.taskCount, color: DONUT_COLORS[i % DONUT_COLORS.length],
  }));
  const totalTasksByFarm = completedTasksByFarm.reduce((s: number, r: any) => s + r.value, 0);

  // Time on Farm data — LP
  const timeOnFarmLPData = transformTimeOnFarm(timeOnFarmLPRaw);
  const timeOnFarmLPUsers = extractTimeOnFarmUsers(timeOnFarmLPRaw, DONUT_COLORS);

  // Time on Farm data — R&M
  const timeOnFarmRMData = transformTimeOnFarm(timeOnFarmRMRaw);
  const timeOnFarmRMUsers = extractTimeOnFarmUsers(timeOnFarmRMRaw, DONUT_COLORS);

  const renderTimeOnFarmChart = (
    title: string,
    subtitle: string,
    loading: boolean,
    chartData: Record<string, any>[],
    users: { key: string; color: string }[],
    exportPDF: (onSuccess: Function, onError: Function) => void,
    exportXLS: (onSuccess: Function, onError: Function) => void,
    filename: string,
    testId: string
  ) => {
    if (loading) return <WRTabLoader />;
    if (chartData.length === 0) {
      return (
        <ChartCard title={title} subtitle={subtitle} testId={testId}>
          <div className="flex flex-col items-center justify-center" style={{ height: 320 }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M3 9h18" />
              <path d="M9 21V9" />
            </svg>
            <p style={{ fontSize: 14, color: "#9ca3af", marginTop: 12 }}>No data to display</p>
          </div>
        </ChartCard>
      );
    }
    return (
      <TimeOnFarmChart
        title={title}
        subtitle={subtitle}
        users={users}
        baseData={chartData}
        exportPDF={exportPDF}
        exportXLS={exportXLS}
        exportFilename={filename}
      />
    );
  };

  const avgHoursMonthly = [{ v: 3.4 }, { v: 3.1 }, { v: 2.9 }, { v: 2.6 }];

  return (
    <div className="flex flex-col gap-5">
      {/* KPI: Avg. Hours Spent per Task */}
      <div className="grid grid-cols-4 gap-5">
        <div className="bg-white rounded-lg py-4 px-5 flex flex-col" style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
          <div className="flex items-center justify-between">
            <p className="text-[12px] font-medium text-[#6b7280]">Avg. Hours Spent per Task</p>
            <Dropdown position="end">
              <Dropdown.Button>
                <button style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>Export</button>
              </Dropdown.Button>
              <Dropdown.Content>
                <Menu onClick={handleExportAvgHoursPDF} disabled={isExportingAvgHoursPDF}>{isExportingAvgHoursPDF ? "Exporting..." : "PDF"}</Menu>
                <Menu onClick={handleExportAvgHoursXLS} disabled={isExportingAvgHoursXLS}>{isExportingAvgHoursXLS ? "Exporting..." : "XLS"}</Menu>
              </Dropdown.Content>
            </Dropdown>
          </div>
          <div className="flex items-center justify-between mt-1 h-[56px]">
            <p className="text-[28px] font-bold text-[#111827] leading-tight">
              {avgHoursKpiLoading ? "..." : avgHoursKpiData ? `${avgHoursKpiData.avgHoursSpentPerTask} hrs` : "—"}
            </p>
            <div className="w-[70px] h-[50px] shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={avgHoursMonthly} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
                  <Bar dataKey="v" fill="#16a34a" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <p className={`text-[11px] mt-auto pt-1 ${avgHoursKpiData && avgHoursKpiData.hoursChange <= 0 ? "text-[#16a34a]" : "text-[#dc2626]"}`}>
            {avgHoursKpiData ? `${avgHoursKpiData.hoursChange >= 0 ? "+" : ""}${avgHoursKpiData.hoursChange} hrs vs last period` : ""}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

        {/* Completed Tasks by R&M — Donut */}
        <ChartCard title="Completed Tasks by R&M" subtitle={`R&M and R&M Management — ${wrPeriodLabels[timePeriod]}`}
          rightHeader={
            <Dropdown position="end">
              <Dropdown.Button>
                <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                  Export
                </button>
              </Dropdown.Button>
              <Dropdown.Content>
                <Menu onClick={handleExportTasksRMPDF} disabled={isExportingTasksRMPDF}>
                  {isExportingTasksRMPDF ? "Exporting PDF..." : "PDF"}
                </Menu>
                <Menu onClick={handleExportTasksRMXLS} disabled={isExportingTasksRMXLS}>
                  {isExportingTasksRMXLS ? "Exporting XLS..." : "Excel"}
                </Menu>
              </Dropdown.Content>
            </Dropdown>
          }
        >
          {completedTasksRMLoading ? (
            <div className="flex items-center justify-center" style={{ height: 260 }}><WRTabLoader /></div>
          ) : completedTasksRM.length === 0 ? (
            <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "40px 0" }}>No completed tasks in this period</p>
          ) : (
            <div className="flex items-center justify-center gap-4">
              <div className="relative shrink-0" style={{ width: 240, height: 260 }}>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={completedTasksRM} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={100}>
                      {completedTasksRM.map((d: any, i: number) => <Cell key={i} fill={d.color} stroke="white" strokeWidth={2} />)}
                    </Pie>
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const p = payload[0];
                        const pct = totalRM > 0 ? ((Number(p.value) / totalRM) * 100).toFixed(1) : "0";
                        return (
                          <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 12px", fontSize: 12 }}>
                            <p style={{ fontWeight: 600 }}>{p.name}</p>
                            <p>{p.value} tasks ({pct}%)</p>
                          </div>
                        );
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span style={{ fontSize: 22, fontWeight: 700, color: "#111827" }}>{totalRM}</span>
                  <span style={{ fontSize: 11, color: "#6b7280" }}>Tasks</span>
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                {completedTasksRM.map((d: any) => (
                  <div key={d.name} className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: d.color }} />
                    <span style={{ fontSize: 12, color: "#6b7280" }}>{d.name} — {d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </ChartCard>

        {/* Completed Tasks by Live Prod. Mgmt — Donut */}
        <ChartCard title="Completed Tasks by Live Prod. Mgmt" subtitle={`Live Production Management — ${wrPeriodLabels[timePeriod]}`}
          rightHeader={
            <Dropdown position="end">
              <Dropdown.Button>
                <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                  Export
                </button>
              </Dropdown.Button>
              <Dropdown.Content>
                <Menu onClick={handleExportTasksLPPDF} disabled={isExportingTasksLPPDF}>
                  {isExportingTasksLPPDF ? "Exporting PDF..." : "PDF"}
                </Menu>
                <Menu onClick={handleExportTasksLPXLS} disabled={isExportingTasksLPXLS}>
                  {isExportingTasksLPXLS ? "Exporting XLS..." : "Excel"}
                </Menu>
              </Dropdown.Content>
            </Dropdown>
          }
        >
          {completedTasksLPLoading ? (
            <div className="flex items-center justify-center" style={{ height: 260 }}><WRTabLoader /></div>
          ) : completedTasksLP.length === 0 ? (
            <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "40px 0" }}>No completed tasks in this period</p>
          ) : (
            <div className="flex items-center justify-center gap-4">
              <div className="relative shrink-0" style={{ width: 240, height: 260 }}>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={completedTasksLP} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={100}>
                      {completedTasksLP.map((d: any, i: number) => <Cell key={i} fill={d.color} stroke="white" strokeWidth={2} />)}
                    </Pie>
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const p = payload[0];
                        const pct = totalLP > 0 ? ((Number(p.value) / totalLP) * 100).toFixed(1) : "0";
                        return (
                          <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 12px", fontSize: 12 }}>
                            <p style={{ fontWeight: 600 }}>{p.name}</p>
                            <p>{p.value} tasks ({pct}%)</p>
                          </div>
                        );
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span style={{ fontSize: 22, fontWeight: 700, color: "#111827" }}>{totalLP}</span>
                  <span style={{ fontSize: 11, color: "#6b7280" }}>Tasks</span>
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                {completedTasksLP.map((d: any) => (
                  <div key={d.name} className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: d.color }} />
                    <span style={{ fontSize: 12, color: "#6b7280" }}>{d.name} — {d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </ChartCard>

        {/* Completed Tasks by Growers — Donut */}
        <ChartCard title="Completed Tasks by Growers" subtitle={`Growers — ${wrPeriodLabels[timePeriod]}`}
          rightHeader={
            <Dropdown position="end">
              <Dropdown.Button>
                <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                  Export
                </button>
              </Dropdown.Button>
              <Dropdown.Content>
                <Menu onClick={handleExportTasksGrowerPDF} disabled={isExportingTasksGrowerPDF}>
                  {isExportingTasksGrowerPDF ? "Exporting PDF..." : "PDF"}
                </Menu>
                <Menu onClick={handleExportTasksGrowerXLS} disabled={isExportingTasksGrowerXLS}>
                  {isExportingTasksGrowerXLS ? "Exporting XLS..." : "Excel"}
                </Menu>
              </Dropdown.Content>
            </Dropdown>
          }
        >
          {completedTasksGrowerLoading ? (
            <div className="flex items-center justify-center" style={{ height: 260 }}><WRTabLoader /></div>
          ) : completedTasksGrower.length === 0 ? (
            <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "40px 0" }}>No completed tasks in this period</p>
          ) : (
            <div className="flex items-center justify-center gap-4">
              <div className="relative shrink-0" style={{ width: 240, height: 260 }}>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={completedTasksGrower} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={100}>
                      {completedTasksGrower.map((d: any, i: number) => <Cell key={i} fill={d.color} stroke="white" strokeWidth={2} />)}
                    </Pie>
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const p = payload[0];
                        const pct = totalGrower > 0 ? ((Number(p.value) / totalGrower) * 100).toFixed(1) : "0";
                        return (
                          <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 12px", fontSize: 12 }}>
                            <p style={{ fontWeight: 600 }}>{p.name}</p>
                            <p>{p.value} tasks ({pct}%)</p>
                          </div>
                        );
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span style={{ fontSize: 22, fontWeight: 700, color: "#111827" }}>{totalGrower}</span>
                  <span style={{ fontSize: 11, color: "#6b7280" }}>Tasks</span>
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                {completedTasksGrower.map((d: any) => (
                  <div key={d.name} className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: d.color }} />
                    <span style={{ fontSize: 12, color: "#6b7280" }}>{d.name} — {d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </ChartCard>

        {/* Completed Tasks by Farm — Donut */}
        <ChartCard title="Completed Tasks by Farm" subtitle={`Closed-Complete tasks per facility — ${wrPeriodLabels[timePeriod]}`}
          rightHeader={
            <Dropdown position="end">
              <Dropdown.Button>
                <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                  Export
                </button>
              </Dropdown.Button>
              <Dropdown.Content>
                <Menu onClick={handleExportTasksByFarmPDF} disabled={isExportingTasksByFarmPDF}>
                  {isExportingTasksByFarmPDF ? "Exporting PDF..." : "PDF"}
                </Menu>
                <Menu onClick={handleExportTasksByFarmXLS} disabled={isExportingTasksByFarmXLS}>
                  {isExportingTasksByFarmXLS ? "Exporting XLS..." : "Excel"}
                </Menu>
              </Dropdown.Content>
            </Dropdown>
          }
        >
          {completedTasksByFarmLoading ? (
            <div className="flex items-center justify-center" style={{ height: 260 }}><WRTabLoader /></div>
          ) : completedTasksByFarm.length === 0 ? (
            <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "40px 0" }}>No completed tasks in this period</p>
          ) : (
            <div className="flex items-center justify-center gap-4">
              <div className="relative shrink-0" style={{ width: 240, height: 260 }}>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={completedTasksByFarm} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={100}>
                      {completedTasksByFarm.map((d: any, i: number) => <Cell key={i} fill={d.color} stroke="white" strokeWidth={2} />)}
                    </Pie>
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const p = payload[0];
                        const pct = totalTasksByFarm > 0 ? ((Number(p.value) / totalTasksByFarm) * 100).toFixed(1) : "0";
                        return (
                          <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 12px", fontSize: 12 }}>
                            <p style={{ fontWeight: 600 }}>{p.name}</p>
                            <p>{p.value} tasks ({pct}%)</p>
                          </div>
                        );
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span style={{ fontSize: 22, fontWeight: 700, color: "#111827" }}>{totalTasksByFarm}</span>
                  <span style={{ fontSize: 11, color: "#6b7280" }}>Tasks</span>
                </div>
              </div>
              <div className="flex flex-col gap-1.5" style={{ maxHeight: 240, overflowY: "auto" }}>
                {completedTasksByFarm.map((d: any) => (
                  <div key={d.name} className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: d.color }} />
                    <span style={{ fontSize: 12, color: "#6b7280" }}>{d.name} — {d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </ChartCard>

      </div>

      {/* PM vs Reactive Tasks */}
      <ChartCard title="PM vs Reactive Tasks" subtitle={`Preventive Maintenance (from Scheduler) vs Reactive (all other) tasks — ${wrPeriodLabels[timePeriod]}`}
        rightHeader={
          <Dropdown position="end">
            <Dropdown.Button>
              <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                Export
              </button>
            </Dropdown.Button>
            <Dropdown.Content>
              <Menu onClick={handleExportPMReactivePDF} disabled={isExportingPMReactivePDF}>
                {isExportingPMReactivePDF ? "Exporting PDF..." : "PDF"}
              </Menu>
              <Menu onClick={handleExportPMReactiveXLS} disabled={isExportingPMReactiveXLS}>
                {isExportingPMReactiveXLS ? "Exporting XLS..." : "Excel"}
              </Menu>
            </Dropdown.Content>
          </Dropdown>
        }
      >
        {pmReactiveData.length === 0 ? (
          <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "40px 0" }}>No data in this period</p>
        ) : (
          <div className="flex flex-col">
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={pmReactiveData} margin={{ top: 5, right: 10, left: -10, bottom: 15 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis dataKey="week" tick={<PMXAxisTick />} height={42} />
                <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} label={{ value: "Tasks", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "#6b7280" }, offset: 15 }} />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const pm = Number(payload.find((p) => p.dataKey === "PM")?.value || 0);
                    const re = Number(payload.find((p) => p.dataKey === "Reactive")?.value || 0);
                    return (
                      <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 12px", fontSize: 12 }}>
                        <p style={{ fontWeight: 600, marginBottom: 4 }}>{(label as string).split("|")[0]}</p>
                        <p style={{ color: "#16a34a", margin: "2px 0" }}>PM: {pm} tasks</p>
                        <p style={{ color: "#3b82f6", margin: "2px 0" }}>Reactive: {re} tasks</p>
                        <p style={{ fontWeight: 600, marginTop: 4, borderTop: "1px solid #e5e7eb", paddingTop: 4 }}>
                          Total: {pm + re} tasks
                        </p>
                      </div>
                    );
                  }}
                />
                <Bar dataKey="PM" stackId="a" fill="#16a34a" />
                <Bar dataKey="Reactive" stackId="a" fill="#3b82f6" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-x-6 gap-y-1 justify-center pt-2">
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: "#16a34a" }} />
                <span style={{ fontSize: 12, color: "#6b7280" }}>PM (Preventive Maintenance)</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: "#3b82f6" }} />
                <span style={{ fontSize: 12, color: "#6b7280" }}>Reactive</span>
              </div>
            </div>
          </div>
        )}
      </ChartCard>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {renderTimeOnFarmChart(
          "Time on Farm - Live Production",
          `Live Production users — hours per facility — ${wrPeriodLabels[timePeriod]}`,
          timeOnFarmLPLoading,
          timeOnFarmLPData,
          timeOnFarmLPUsers,
          (onSuccess: Function, onError: Function) => dashboardApi.exportManagerHoursByFacilityLPToPDF({ Days: days }, onSuccess, onError),
          (onSuccess: Function, onError: Function) => dashboardApi.exportManagerHoursByFacilityLPToXLS({ Days: days }, onSuccess, onError),
          "time_on_farm_lp",
          "chart-time-on-farm-lp"
        )}
        {renderTimeOnFarmChart(
          "Time on Farm - R&M",
          `R&M and R&M Management users — hours per facility — ${wrPeriodLabels[timePeriod]}`,
          timeOnFarmRMLoading,
          timeOnFarmRMData,
          timeOnFarmRMUsers,
          (onSuccess: Function, onError: Function) => dashboardApi.exportManagerHoursByFacilityRMToPDF({ Days: days }, onSuccess, onError),
          (onSuccess: Function, onError: Function) => dashboardApi.exportManagerHoursByFacilityRMToXLS({ Days: days }, onSuccess, onError),
          "time_on_farm_rm",
          "chart-time-on-farm-rm"
        )}
      </div>

      {/* Logged Hours by Role — 3 columns */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {/* Logged Hours by R&M */}
        <ChartCard title="Logged Hours by R&M" subtitle={`R&M and R&M Management — ${wrPeriodLabels[timePeriod]}`}
          rightHeader={
            <Dropdown position="end">
              <Dropdown.Button>
                <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>Export</button>
              </Dropdown.Button>
              <Dropdown.Content>
                <Menu onClick={handleExportLoggedHoursRMPDF} disabled={isExportingLoggedHoursRMPDF}>{isExportingLoggedHoursRMPDF ? "Exporting PDF..." : "PDF"}</Menu>
                <Menu onClick={handleExportLoggedHoursRMXLS} disabled={isExportingLoggedHoursRMXLS}>{isExportingLoggedHoursRMXLS ? "Exporting XLS..." : "Excel"}</Menu>
              </Dropdown.Content>
            </Dropdown>
          }
        >
          {loggedHoursRMLoading ? (
            <div className="flex items-center justify-center" style={{ height: 260 }}><WRTabLoader /></div>
          ) : loggedHoursRM.length === 0 ? (
            <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "40px 0" }}>No logged hours in this period</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={loggedHoursRM} margin={{ top: 5, right: 10, left: -10, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#6b7280" }} angle={-35} textAnchor="end" interval={0} />
                <YAxis tick={{ fontSize: 10, fill: "#6b7280" }} label={{ value: "Hours", angle: -90, position: "insideLeft", style: { fontSize: 10, fill: "#6b7280" }, offset: 15 }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb" }} formatter={(value: any) => [`${value} hrs`, "Hours logged"]} />
                <Bar dataKey="hours" fill="#2B5A2E" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        {/* Logged Hours by Live Production */}
        <ChartCard title="Logged Hours by Live Prod." subtitle={`Live Production Management — ${wrPeriodLabels[timePeriod]}`}
          rightHeader={
            <Dropdown position="end">
              <Dropdown.Button>
                <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>Export</button>
              </Dropdown.Button>
              <Dropdown.Content>
                <Menu onClick={handleExportLoggedHoursLPPDF} disabled={isExportingLoggedHoursLPPDF}>{isExportingLoggedHoursLPPDF ? "Exporting PDF..." : "PDF"}</Menu>
                <Menu onClick={handleExportLoggedHoursLPXLS} disabled={isExportingLoggedHoursLPXLS}>{isExportingLoggedHoursLPXLS ? "Exporting XLS..." : "Excel"}</Menu>
              </Dropdown.Content>
            </Dropdown>
          }
        >
          {loggedHoursLPLoading ? (
            <div className="flex items-center justify-center" style={{ height: 260 }}><WRTabLoader /></div>
          ) : loggedHoursLP.length === 0 ? (
            <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "40px 0" }}>No logged hours in this period</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={loggedHoursLP} margin={{ top: 5, right: 10, left: -10, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#6b7280" }} angle={-35} textAnchor="end" interval={0} />
                <YAxis tick={{ fontSize: 10, fill: "#6b7280" }} label={{ value: "Hours", angle: -90, position: "insideLeft", style: { fontSize: 10, fill: "#6b7280" }, offset: 15 }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb" }} formatter={(value: any) => [`${value} hrs`, "Hours logged"]} />
                <Bar dataKey="hours" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        {/* Logged Hours by Growers */}
        <ChartCard title="Logged Hours by Growers" subtitle={`Growers — ${wrPeriodLabels[timePeriod]}`}
          rightHeader={
            <Dropdown position="end">
              <Dropdown.Button>
                <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>Export</button>
              </Dropdown.Button>
              <Dropdown.Content>
                <Menu onClick={handleExportLoggedHoursGrowerPDF} disabled={isExportingLoggedHoursGrowerPDF}>{isExportingLoggedHoursGrowerPDF ? "Exporting PDF..." : "PDF"}</Menu>
                <Menu onClick={handleExportLoggedHoursGrowerXLS} disabled={isExportingLoggedHoursGrowerXLS}>{isExportingLoggedHoursGrowerXLS ? "Exporting XLS..." : "Excel"}</Menu>
              </Dropdown.Content>
            </Dropdown>
          }
        >
          {loggedHoursGrowerLoading ? (
            <div className="flex items-center justify-center" style={{ height: 260 }}><WRTabLoader /></div>
          ) : loggedHoursGrower.length === 0 ? (
            <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "40px 0" }}>No logged hours in this period</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={loggedHoursGrower} margin={{ top: 5, right: 10, left: -10, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#6b7280" }} angle={-35} textAnchor="end" interval={0} />
                <YAxis tick={{ fontSize: 10, fill: "#6b7280" }} label={{ value: "Hours", angle: -90, position: "insideLeft", style: { fontSize: 10, fill: "#6b7280" }, offset: 15 }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb" }} formatter={(value: any) => [`${value} hrs`, "Hours logged"]} />
                <Bar dataKey="hours" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>
    </div>
  );
}

const purchasingMethodData = [
  { week: "Wk 1", Emergency: 35, StockPick: 65 }, { week: "Wk 2", Emergency: 28, StockPick: 72 },
  { week: "Wk 3", Emergency: 40, StockPick: 60 }, { week: "Wk 4", Emergency: 32, StockPick: 68 },
  { week: "Wk 5", Emergency: 38, StockPick: 62 }, { week: "Wk 6", Emergency: 25, StockPick: 75 },
  { week: "Wk 7", Emergency: 30, StockPick: 70 }, { week: "Wk 8", Emergency: 36, StockPick: 64 },
  { week: "Wk 9", Emergency: 29, StockPick: 71 }, { week: "Wk 10", Emergency: 33, StockPick: 67 },
  { week: "Wk 11", Emergency: 27, StockPick: 73 }, { week: "Wk 12", Emergency: 31, StockPick: 69 },
];

const purchasingSpendData = [
  { month: "Jan 2025", emergency: 4820, stock: 9240, total: 14060 },
  { month: "Feb 2025", emergency: 3650, stock: 8780, total: 12430 },
  { month: "Mar 2025", emergency: 5210, stock: 10120, total: 15330 },
  { month: "Apr 2025", emergency: 4180, stock: 9560, total: 13740 },
  { month: "May 2025", emergency: 6340, stock: 11280, total: 17620 },
  { month: "Jun 2025", emergency: 3920, stock: 8940, total: 12860 },
  { month: "Jul 2025", emergency: 5780, stock: 10640, total: 16420 },
  { month: "Aug 2025", emergency: 4460, stock: 9380, total: 13840 },
  { month: "Sep 2025", emergency: 6120, stock: 11560, total: 17680 },
  { month: "Oct 2025", emergency: 5290, stock: 10840, total: 16130 },
  { month: "Nov 2025", emergency: 4710, stock: 9720, total: 14430 },
  { month: "Dec 2025", emergency: 5960, stock: 11280, total: 17240 },
];

const spendTotals = {
  emergency: purchasingSpendData.reduce((s, r) => s + r.emergency, 0),
  stock: purchasingSpendData.reduce((s, r) => s + r.stock, 0),
  total: purchasingSpendData.reduce((s, r) => s + r.total, 0),
};

function fmt$(n: number) {
  return "$" + n.toLocaleString();
}

function PurchasingTab({ days, timePeriod }: { days: number; timePeriod: WRTimePeriod }) {
  const dashboardApi = useDashboardApi();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [byWORaw, setByWORaw] = useState<any[]>([]);
  const [byWOLoading, setByWOLoading] = useState(true);
  const [isExportingCountPDF, setIsExportingCountPDF] = useState(false);
  const [isExportingCountXLS, setIsExportingCountXLS] = useState(false);
  const [isExportingValuePDF, setIsExportingValuePDF] = useState(false);
  const [isExportingValueXLS, setIsExportingValueXLS] = useState(false);
  const [isExportingGoodsOnOrderPDF, setIsExportingGoodsOnOrderPDF] = useState(false);
  const [isExportingGoodsOnOrderXLS, setIsExportingGoodsOnOrderXLS] = useState(false);
  const [isExportingPurchaseReceiptsPDF, setIsExportingPurchaseReceiptsPDF] = useState(false);
  const [isExportingPurchaseReceiptsXLS, setIsExportingPurchaseReceiptsXLS] = useState(false);
  const [isExportingLowInventoryPDF, setIsExportingLowInventoryPDF] = useState(false);
  const [isExportingLowInventoryXLS, setIsExportingLowInventoryXLS] = useState(false);

  useEffect(() => {
    setLoading(true);
    setByWOLoading(true);
    dashboardApi.getManagerDashboardPurchasing(
      { Days: days },
      (response: any) => {
        const d = response?.data || response;
        setData(d);
        setLoading(false);
      },
      (error: any) => { console.error("Failed to fetch purchasing:", error); setLoading(false); }
    );
    dashboardApi.getPurchasingMethodByWO(
      { Days: days },
      (response: any) => {
        const rows = response?.data || response || [];
        setByWORaw(Array.isArray(rows) ? rows : []);
        setByWOLoading(false);
      },
      () => { setByWOLoading(false); }
    );
  }, [days]);

  if (loading || byWOLoading) return <WRTabLoader />;

  const goodsOnOrder = data?.goodsOnOrder || [];
  const purchaseReceipts = data?.purchaseReceipts || [];
  const lowInventory = data?.lowInventory || [];
  const criticalCount = lowInventory.filter((r: any) => r.stockStatus === "Critical").length;
  const lowCount = lowInventory.filter((r: any) => r.stockStatus === "Low").length;

  const byWOData = byWORaw.map((r: any) => ({ ...r, week: r.Week ?? r.week }));

  const handleExportCountPDF = () => {
    setIsExportingCountPDF(true);
    dashboardApi.exportPurchasingMethodCountToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'stock_usage_count'); setIsExportingCountPDF(false); },
      () => { setIsExportingCountPDF(false); }
    );
  };
  const handleExportCountXLS = () => {
    setIsExportingCountXLS(true);
    dashboardApi.exportPurchasingMethodCountToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'stock_usage_count'); setIsExportingCountXLS(false); },
      () => { setIsExportingCountXLS(false); }
    );
  };
  const handleExportValuePDF = () => {
    setIsExportingValuePDF(true);
    dashboardApi.exportPurchasingMethodValueToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'stock_usage_value'); setIsExportingValuePDF(false); },
      () => { setIsExportingValuePDF(false); }
    );
  };
  const handleExportValueXLS = () => {
    setIsExportingValueXLS(true);
    dashboardApi.exportPurchasingMethodValueToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'stock_usage_value'); setIsExportingValueXLS(false); },
      () => { setIsExportingValueXLS(false); }
    );
  };

  const handleExportPurchaseReceiptsPDF = () => {
    setIsExportingPurchaseReceiptsPDF(true);
    dashboardApi.exportPurchaseReceiptsToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'purchase_receipts'); setIsExportingPurchaseReceiptsPDF(false); },
      () => { setIsExportingPurchaseReceiptsPDF(false); }
    );
  };
  const handleExportPurchaseReceiptsXLS = () => {
    setIsExportingPurchaseReceiptsXLS(true);
    dashboardApi.exportPurchaseReceiptsToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'purchase_receipts'); setIsExportingPurchaseReceiptsXLS(false); },
      () => { setIsExportingPurchaseReceiptsXLS(false); }
    );
  };

  const handleExportGoodsOnOrderPDF = () => {
    setIsExportingGoodsOnOrderPDF(true);
    dashboardApi.exportGoodsOnOrderToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'goods_on_order'); setIsExportingGoodsOnOrderPDF(false); },
      () => { setIsExportingGoodsOnOrderPDF(false); }
    );
  };
  const handleExportGoodsOnOrderXLS = () => {
    setIsExportingGoodsOnOrderXLS(true);
    dashboardApi.exportGoodsOnOrderToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'goods_on_order'); setIsExportingGoodsOnOrderXLS(false); },
      () => { setIsExportingGoodsOnOrderXLS(false); }
    );
  };

  const handleExportLowInventoryPDF = () => {
    setIsExportingLowInventoryPDF(true);
    dashboardApi.exportLowInventoryToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'low_inventory'); setIsExportingLowInventoryPDF(false); },
      () => { setIsExportingLowInventoryPDF(false); }
    );
  };
  const handleExportLowInventoryXLS = () => {
    setIsExportingLowInventoryXLS(true);
    dashboardApi.exportLowInventoryToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'low_inventory'); setIsExportingLowInventoryXLS(false); },
      () => { setIsExportingLowInventoryXLS(false); }
    );
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <ChartCard
          title="Purchasing Method — Count"
          subtitle={`Stock picks vs emergency purchases (items) — ${wrPeriodLabels[timePeriod]}`}
          rightHeader={
            <Dropdown position="end">
              <Dropdown.Button>
                <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>Export</button>
              </Dropdown.Button>
              <Dropdown.Content>
                <Menu onClick={handleExportCountPDF} disabled={isExportingCountPDF}>{isExportingCountPDF ? "Exporting PDF..." : "PDF"}</Menu>
                <Menu onClick={handleExportCountXLS} disabled={isExportingCountXLS}>{isExportingCountXLS ? "Exporting XLS..." : "Excel"}</Menu>
              </Dropdown.Content>
            </Dropdown>
          }
        >
          {byWOData.length === 0 ? (
            <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "40px 0" }}>No stock usage data available</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={byWOData} margin={{ top: 4, right: 8, left: -20, bottom: 15 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                <XAxis dataKey="week" tick={<PMXAxisTick />} height={42} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip
                  labelFormatter={(label: string) => (label ?? '').split("|")[0]}
                  formatter={(value: any, name: string) => [value, name === "stockPickCount" ? "Stock Used" : "Emergency Purchase"]}
                  contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb" }}
                />
                <Legend iconType="square" iconSize={10}
                  formatter={(value) => <span style={{ fontSize: 12, color: "#374151" }}>{value === "stockPickCount" ? "Stock Used" : "Emergency Purchase"}</span>}
                />
                <Bar dataKey="stockPickCount" fill="#16a34a" radius={[2, 2, 0, 0]} />
                <Bar dataKey="emergencyCount" fill="#dc2626" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard
          title="Purchasing Method — Value"
          subtitle={`Stock picks vs emergency purchases ($) — ${wrPeriodLabels[timePeriod]}`}
          rightHeader={
            <Dropdown position="end">
              <Dropdown.Button>
                <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>Export</button>
              </Dropdown.Button>
              <Dropdown.Content>
                <Menu onClick={handleExportValuePDF} disabled={isExportingValuePDF}>{isExportingValuePDF ? "Exporting PDF..." : "PDF"}</Menu>
                <Menu onClick={handleExportValueXLS} disabled={isExportingValueXLS}>{isExportingValueXLS ? "Exporting XLS..." : "Excel"}</Menu>
              </Dropdown.Content>
            </Dropdown>
          }
        >
          {byWOData.length === 0 ? (
            <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "40px 0" }}>No stock usage data available</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={byWOData} margin={{ top: 4, right: 8, left: 10, bottom: 15 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                <XAxis dataKey="week" tick={<PMXAxisTick />} height={42} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={(v) => `$${v.toLocaleString()}`} tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                <Tooltip
                  labelFormatter={(label: string) => (label ?? '').split("|")[0]}
                  formatter={(value: any, name: string) => [`$${Number(value).toLocaleString()}`, name === "stockPickValue" ? "Stock Used" : "Emergency Purchase"]}
                  contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb" }}
                />
                <Legend iconType="square" iconSize={10}
                  formatter={(value) => <span style={{ fontSize: 12, color: "#374151" }}>{value === "stockPickValue" ? "Stock Used" : "Emergency Purchase"}</span>}
                />
                <Bar dataKey="stockPickValue" fill="#16a34a" radius={[2, 2, 0, 0]} />
                <Bar dataKey="emergencyValue" fill="#dc2626" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <ChartCard
          title="Goods on Order"
          subtitle={`PO line items not yet received — ${wrPeriodLabels[timePeriod]}`}
          rightHeader={
            <Dropdown position="end">
              <Dropdown.Button>
                <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                  Export
                </button>
              </Dropdown.Button>
              <Dropdown.Content>
                <Menu onClick={handleExportGoodsOnOrderPDF} disabled={isExportingGoodsOnOrderPDF}>
                  {isExportingGoodsOnOrderPDF ? "Exporting PDF..." : "PDF"}
                </Menu>
                <Menu onClick={handleExportGoodsOnOrderXLS} disabled={isExportingGoodsOnOrderXLS}>
                  {isExportingGoodsOnOrderXLS ? "Exporting XLS..." : "Excel"}
                </Menu>
              </Dropdown.Content>
            </Dropdown>
          }
        >
          {goodsOnOrder.length === 0 ? (
            <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "40px 0" }}>No outstanding purchase orders</p>
          ) : (
            <>
              <div style={{ overflowX: "auto", maxHeight: 400, overflowY: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {["Part Name", "Qty. Ordered", "Qty. Pending", "Unit Price", "Total", "Vendor", "Needed By"].map((h) => (
                        <th key={h} style={{ background: "#f9fafb", fontSize: 12, fontWeight: 600, color: "#6b7280", padding: "10px 12px", textAlign: "left", position: "sticky", top: 0 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {goodsOnOrder.map((row: any, i: number) => (
                      <tr key={row.purchaseOrderID + '-' + row.partsID} style={{ background: i % 2 === 1 ? "#fafafa" : "white" }}>
                        <td style={{ fontSize: 13, padding: "10px 12px", color: "#111827" }}>{row.partsName}</td>
                        <td style={{ fontSize: 13, padding: "10px 12px", color: "#111827" }}>{row.qtyOrdered}</td>
                        <td style={{ fontSize: 13, padding: "10px 12px", color: "#111827" }}>{row.qtyPending}</td>
                        <td style={{ fontSize: 13, padding: "10px 12px", color: "#111827" }}>${Number(row.unitPrice || 0).toLocaleString()}</td>
                        <td style={{ fontSize: 13, padding: "10px 12px", fontWeight: 600, color: "#111827" }}>${Number(row.totalAmount || 0).toLocaleString()}</td>
                        <td style={{ fontSize: 13, padding: "10px 12px", color: "#111827" }}>{row.vendorName}</td>
                        <td style={{ fontSize: 13, padding: "10px 12px", color: "#6b7280" }}>{row.requiredByDate ? new Date(row.requiredByDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ paddingTop: 12, borderTop: "1px solid #f3f4f6", marginTop: 12 }}>
                <span style={{ fontSize: 12, color: "#6b7280" }}>{goodsOnOrder.length} item{goodsOnOrder.length !== 1 ? "s" : ""} on order</span>
              </div>
            </>
          )}
        </ChartCard>

        <ChartCard
          title="Purchase Receipts"
          subtitle={`Parts received from suppliers — ${wrPeriodLabels[timePeriod]}`}
          rightHeader={
            <Dropdown position="end">
              <Dropdown.Button>
                <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                  Export
                </button>
              </Dropdown.Button>
              <Dropdown.Content>
                <Menu onClick={handleExportPurchaseReceiptsPDF} disabled={isExportingPurchaseReceiptsPDF}>
                  {isExportingPurchaseReceiptsPDF ? "Exporting PDF..." : "PDF"}
                </Menu>
                <Menu onClick={handleExportPurchaseReceiptsXLS} disabled={isExportingPurchaseReceiptsXLS}>
                  {isExportingPurchaseReceiptsXLS ? "Exporting XLS..." : "Excel"}
                </Menu>
              </Dropdown.Content>
            </Dropdown>
          }
        >
          {purchaseReceipts.length === 0 ? (
            <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "40px 0" }}>No receipts in selected period</p>
          ) : (
            <>
              <div style={{ overflowX: "auto", maxHeight: 400, overflowY: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {["Part Name", "Qty. Ordered", "Qty. Received", "Unit Price", "Total", "Vendor", "Received Date"].map((h) => (
                        <th key={h} style={{ background: "#f9fafb", fontSize: 12, fontWeight: 600, color: "#6b7280", padding: "10px 12px", textAlign: "left", position: "sticky", top: 0 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {purchaseReceipts.map((row: any, i: number) => (
                      <tr key={row.receiptID + '-' + row.partsID} style={{ background: i % 2 === 1 ? "#fafafa" : "white" }}>
                        <td style={{ fontSize: 13, padding: "10px 12px", color: "#111827" }}>{row.partsName}</td>
                        <td style={{ fontSize: 13, padding: "10px 12px", color: "#111827" }}>{row.qtyOrdered}</td>
                        <td style={{ fontSize: 13, padding: "10px 12px", color: "#111827" }}>{row.qtyReceived}</td>
                        <td style={{ fontSize: 13, padding: "10px 12px", color: "#111827" }}>${Number(row.unitPrice || 0).toLocaleString()}</td>
                        <td style={{ fontSize: 13, padding: "10px 12px", fontWeight: 600, color: "#111827" }}>${Number(row.totalAmount || 0).toLocaleString()}</td>
                        <td style={{ fontSize: 13, padding: "10px 12px", color: "#111827" }}>{row.vendorName}</td>
                        <td style={{ fontSize: 13, padding: "10px 12px", color: "#6b7280" }}>{row.receiptDate ? new Date(row.receiptDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ paddingTop: 12, borderTop: "1px solid #f3f4f6", marginTop: 12 }}>
                <span style={{ fontSize: 12, color: "#6b7280" }}>{purchaseReceipts.length} receipt{purchaseReceipts.length !== 1 ? "s" : ""}</span>
              </div>
            </>
          )}
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 gap-5">
        <ChartCard
          title="Low Inventory"
          subtitle="Parts at or below reorder point"
          rightHeader={
            <Dropdown position="end">
              <Dropdown.Button>
                <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                  Export
                </button>
              </Dropdown.Button>
              <Dropdown.Content>
                <Menu onClick={handleExportLowInventoryPDF} disabled={isExportingLowInventoryPDF}>
                  {isExportingLowInventoryPDF ? "Exporting PDF..." : "PDF"}
                </Menu>
                <Menu onClick={handleExportLowInventoryXLS} disabled={isExportingLowInventoryXLS}>
                  {isExportingLowInventoryXLS ? "Exporting XLS..." : "Excel"}
                </Menu>
              </Dropdown.Content>
            </Dropdown>
          }
        >
          {lowInventory.length === 0 ? (
            <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "40px 0" }}>All parts above reorder point</p>
          ) : (
            <>
              <div style={{ overflowX: "auto", maxHeight: 400, overflowY: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {["Part Name", "Qty on Hand", "Min Qty", "Facility", "Status"].map((h) => (
                        <th key={h} style={{ background: "#f9fafb", fontSize: 12, fontWeight: 600, color: "#6b7280", padding: "10px 12px", textAlign: "left", position: "sticky", top: 0 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {lowInventory.map((row: any, i: number) => (
                      <tr key={row.partsID} style={{ background: i % 2 === 1 ? "#fafafa" : "white" }}>
                        <td style={{ fontSize: 13, padding: "10px 12px", color: "#111827" }}>{row.partsName}</td>
                        <td style={{ fontSize: 13, padding: "10px 12px", color: "#111827" }}>{row.qtyOnHand}</td>
                        <td style={{ fontSize: 13, padding: "10px 12px", color: "#111827" }}>{row.minStock}</td>
                        <td style={{ fontSize: 13, padding: "10px 12px", color: "#6b7280" }}>{row.facilityName}</td>
                        <td style={{ fontSize: 13, padding: "10px 12px" }}>
                          <span
                            style={{
                              background: row.stockStatus === "Critical" ? "#fee2e2" : "#ffedd5",
                              color: row.stockStatus === "Critical" ? "#dc2626" : "#ea580c",
                              borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 600,
                            }}
                          >
                            {row.stockStatus}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ paddingTop: 12, borderTop: "1px solid #f3f4f6", marginTop: 12 }}>
                <span style={{ fontSize: 12, color: "#dc2626" }}>{criticalCount} Critical · {lowCount} Low</span>
              </div>
            </>
          )}
        </ChartCard>
      </div>
    </div>
  );
}


const assetDowntimeAssets = [
  { key: "Fan Cover Project", color: "#dc2626" },
  { key: "Climate System Unit 4", color: "#2563eb" },
  { key: "Water Pump Station 2", color: "#16a34a" },
  { key: "Generator Unit 1", color: "#d97706" },
  { key: "Feed Auger System A", color: "#7c3aed" },
  { key: "Ventilation Motor B3", color: "#0891b2" },
  { key: "Heating Element Barn 5", color: "#db2777" },
  { key: "Washer Residence 1", color: "#65a30d" },
  { key: "Dryer Residence 2", color: "#ea580c" },
  { key: "Range/Stove Residence 1", color: "#4f46e5" },
  { key: "Refrigerator Unit 3", color: "#0d9488" },
  { key: "Fan Belt Drive C", color: "#b45309" },
  { key: "Water Line Valve 4", color: "#6d28d9" },
  { key: "Electrical Panel B2", color: "#be185d" },
  { key: "Climate Controller 2", color: "#047857" },
];

const assetDowntimeMonthly = [
  { month: "Aug", "Fan Cover Project": 5, "Climate System Unit 4": 3, "Water Pump Station 2": 4, "Generator Unit 1": 2, "Feed Auger System A": 3, "Ventilation Motor B3": 2, "Heating Element Barn 5": 1, "Washer Residence 1": 2, "Dryer Residence 2": 1, "Range/Stove Residence 1": 1, "Refrigerator Unit 3": 1, "Fan Belt Drive C": 2, "Water Line Valve 4": 1, "Electrical Panel B2": 1, "Climate Controller 2": 0 },
  { month: "Sep", "Fan Cover Project": 4, "Climate System Unit 4": 4, "Water Pump Station 2": 2, "Generator Unit 1": 3, "Feed Auger System A": 2, "Ventilation Motor B3": 3, "Heating Element Barn 5": 2, "Washer Residence 1": 1, "Dryer Residence 2": 2, "Range/Stove Residence 1": 1, "Refrigerator Unit 3": 2, "Fan Belt Drive C": 1, "Water Line Valve 4": 1, "Electrical Panel B2": 0, "Climate Controller 2": 1 },
  { month: "Oct", "Fan Cover Project": 3, "Climate System Unit 4": 2, "Water Pump Station 2": 3, "Generator Unit 1": 3, "Feed Auger System A": 2, "Ventilation Motor B3": 1, "Heating Element Barn 5": 3, "Washer Residence 1": 2, "Dryer Residence 2": 1, "Range/Stove Residence 1": 2, "Refrigerator Unit 3": 1, "Fan Belt Drive C": 1, "Water Line Valve 4": 2, "Electrical Panel B2": 1, "Climate Controller 2": 1 },
  { month: "Nov", "Fan Cover Project": 6, "Climate System Unit 4": 5, "Water Pump Station 2": 4, "Generator Unit 1": 4, "Feed Auger System A": 3, "Ventilation Motor B3": 3, "Heating Element Barn 5": 2, "Washer Residence 1": 3, "Dryer Residence 2": 3, "Range/Stove Residence 1": 2, "Refrigerator Unit 3": 1, "Fan Belt Drive C": 1, "Water Line Valve 4": 1, "Electrical Panel B2": 2, "Climate Controller 2": 1 },
  { month: "Dec", "Fan Cover Project": 3, "Climate System Unit 4": 3, "Water Pump Station 2": 2, "Generator Unit 1": 1, "Feed Auger System A": 2, "Ventilation Motor B3": 1, "Heating Element Barn 5": 2, "Washer Residence 1": 1, "Dryer Residence 2": 1, "Range/Stove Residence 1": 1, "Refrigerator Unit 3": 1, "Fan Belt Drive C": 2, "Water Line Valve 4": 0, "Electrical Panel B2": 1, "Climate Controller 2": 1 },
  { month: "Jan", "Fan Cover Project": 3, "Climate System Unit 4": 2, "Water Pump Station 2": 2, "Generator Unit 1": 2, "Feed Auger System A": 2, "Ventilation Motor B3": 2, "Heating Element Barn 5": 1, "Washer Residence 1": 1, "Dryer Residence 2": 1, "Range/Stove Residence 1": 1, "Refrigerator Unit 3": 1, "Fan Belt Drive C": 0, "Water Line Valve 4": 1, "Electrical Panel B2": 0, "Climate Controller 2": 0 },
];

const mttrMtbfData = [
  { name: "Fan Cover Project", mttr: 38.5, mtbf: 120, mtbfScaled: 12, wrs: 24 },
  { name: "Climate System Unit 4", mttr: 29.2, mtbf: 180, mtbfScaled: 18, wrs: 19 },
  { name: "Water Pump Station 2", mttr: 24.8, mtbf: 210, mtbfScaled: 21, wrs: 17 },
  { name: "Generator Unit 1", mttr: 31.4, mtbf: 150, mtbfScaled: 15, wrs: 15 },
  { name: "Feed Auger System A", mttr: 18.6, mtbf: 240, mtbfScaled: 24, wrs: 14 },
  { name: "Ventilation Motor B3", mttr: 22.1, mtbf: 280, mtbfScaled: 28, wrs: 12 },
  { name: "Heating Element Barn 5", mttr: 15.3, mtbf: 320, mtbfScaled: 32, wrs: 11 },
  { name: "Washer Residence 1", mttr: 8.4, mtbf: 420, mtbfScaled: 42, wrs: 10 },
  { name: "Dryer Residence 2", mttr: 7.9, mtbf: 400, mtbfScaled: 40, wrs: 9 },
  { name: "Range/Stove Residence 1", mttr: 5.2, mtbf: 480, mtbfScaled: 48, wrs: 8 },
  { name: "Refrigerator Unit 3", mttr: 6.8, mtbf: 460, mtbfScaled: 46, wrs: 7 },
  { name: "Fan Belt Drive C", mttr: 12.4, mtbf: 350, mtbfScaled: 35, wrs: 7 },
  { name: "Water Line Valve 4", mttr: 9.7, mtbf: 390, mtbfScaled: 39, wrs: 6 },
  { name: "Electrical Panel B2", mttr: 19.3, mtbf: 260, mtbfScaled: 26, wrs: 5 },
  { name: "Climate Controller 2", mttr: 14.6, mtbf: 310, mtbfScaled: 31, wrs: 4 },
];

function getReliabilityColor(mttr: number, mtbf: number) {
  if (mtbf < 720 && mttr > 40) return "#dc2626";   // Critical: worst MTBF band + worst MTTR band
  if (mtbf > 1440 && mttr < 16) return "#16a34a";  // Reliable: best MTBF band + best MTTR band
  return "#d97706";                                  // Watch list: everything else
}

function clampForPlot(mtbf: number, mttr: number) {
  return { plottedMtbf: Math.min(mtbf, 2160), plottedMttr: Math.min(mttr, 72) };
}


function hoursToHm(val: number): string {
  if (val == null || isNaN(val) || val === 0) return '0h 0m';
  const h = Math.floor(val);
  const m = Math.round((val - h) * 60);
  if (m === 60) return `${h + 1}h 0m`;
  return `${h}h ${m}m`;
}

function AssetDowntimeChart({ days, rawDowntimeData }: { days: number; rawDowntimeData: any[] }) {
  const dashboardApi = useDashboardApi();
  const [selectedAsset, setSelectedAsset] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isExportingDowntimePDF, setIsExportingDowntimePDF] = useState(false);
  const [isExportingDowntimeXLS, setIsExportingDowntimeXLS] = useState(false);

  const handleExportDowntimePDF = () => {
    setIsExportingDowntimePDF(true);
    dashboardApi.exportAssetDowntimeToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'asset_downtime'); setIsExportingDowntimePDF(false); },
      () => { setIsExportingDowntimePDF(false); }
    );
  };
  const handleExportDowntimeXLS = () => {
    setIsExportingDowntimeXLS(true);
    dashboardApi.exportAssetDowntimeToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'asset_downtime'); setIsExportingDowntimeXLS(false); },
      () => { setIsExportingDowntimeXLS(false); }
    );
  };

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Build chart data from API response
  const CHART_COLORS = ["#dc2626","#2563eb","#16a34a","#d97706","#7c3aed","#0891b2","#db2777","#65a30d","#ea580c","#4f46e5","#0d9488","#b45309","#6d28d9","#be185d","#047857"];
  const assetNames = [...new Set(rawDowntimeData.map((r: any) => r.assetName))];
  const months = [...new Set(rawDowntimeData.map((r: any) => r.monthKey))].sort();
  const apiAssets = assetNames.map((name, i) => ({ key: name, color: CHART_COLORS[i % CHART_COLORS.length] }));
  const apiMonthly = months.map((mk) => {
    const label = rawDowntimeData.find((r: any) => r.monthKey === mk)?.monthLabel || mk;
    const row: any = { month: label };
    assetNames.forEach((name) => {
      const match = rawDowntimeData.find((r: any) => r.assetName === name && r.monthKey === mk);
      row[name] = match ? match.downtimeHours : 0;
    });
    return row;
  });

  const filtered = apiAssets.filter((a) => a.key.toLowerCase().includes(search.toLowerCase()));
  const visibleAssets = selectedAsset
    ? apiAssets.filter((a) => a.key === selectedAsset)
    : apiAssets;

  const periodLabel = days <= 7 ? "last 7 days" : days <= 30 ? "last 30 days" : days <= 120 ? "last 120 days" : "last 12 months";

  return (
    <ChartCard
      title="Asset Downtime"
      subtitle={selectedAsset ? `Downtime Hours — ${selectedAsset} (${periodLabel})` : `Downtime hours per asset — top 15 assets (${periodLabel})`}
      rightHeader={
        <div className="flex items-center gap-2">
        <Dropdown position="end">
          <Dropdown.Button>
            <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
              Export
            </button>
          </Dropdown.Button>
          <Dropdown.Content>
            <Menu onClick={handleExportDowntimePDF} disabled={isExportingDowntimePDF}>
              {isExportingDowntimePDF ? "Exporting PDF..." : "PDF"}
            </Menu>
            <Menu onClick={handleExportDowntimeXLS} disabled={isExportingDowntimeXLS}>
              {isExportingDowntimeXLS ? "Exporting XLS..." : "Excel"}
            </Menu>
          </Dropdown.Content>
        </Dropdown>
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setOpen(!open)}
            style={{
              padding: "4px 10px", fontSize: 12, borderRadius: 4, cursor: "pointer",
              background: selectedAsset ? "#2B5A2E" : "white",
              color: selectedAsset ? "white" : "#6b7280",
              border: selectedAsset ? "1px solid #2B5A2E" : "1px solid #e5e7eb",
              minWidth: 160, textAlign: "left",
            }}
          >
            {selectedAsset || "Search asset..."}
          </button>
          {open && (
            <div style={{
              position: "absolute", top: "100%", right: 0, marginTop: 4, width: 240,
              background: "white", border: "1px solid #e5e7eb", borderRadius: 6,
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)", zIndex: 50,
            }}>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Type to search..."
                autoFocus
                style={{
                  width: "100%", padding: "8px 12px", fontSize: 12, border: "none",
                  borderBottom: "1px solid #f3f4f6", outline: "none", boxSizing: "border-box",
                }}
              />
              <div style={{ maxHeight: 200, overflowY: "auto" }}>
                {selectedAsset && (
                  <button
                    onClick={() => { setSelectedAsset(null); setSearch(""); setOpen(false); }}
                    style={{
                      width: "100%", padding: "8px 12px", fontSize: 12, textAlign: "left",
                      cursor: "pointer", background: "#f9fafb", border: "none", color: "#dc2626",
                      borderBottom: "1px solid #f3f4f6",
                    }}
                  >
                    Clear selection (show all)
                  </button>
                )}
                {filtered.map((a) => (
                  <button
                    key={a.key}
                    onClick={() => { setSelectedAsset(a.key); setSearch(""); setOpen(false); }}
                    style={{
                      width: "100%", padding: "8px 12px", fontSize: 12, textAlign: "left",
                      cursor: "pointer", background: selectedAsset === a.key ? "#f0fdf4" : "white",
                      border: "none", borderBottom: "1px solid #f9fafb",
                      display: "flex", alignItems: "center", gap: 8,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "#f9fafb"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = selectedAsset === a.key ? "#f0fdf4" : "white"; }}
                  >
                    <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: a.color }} />
                    {a.key}
                  </button>
                ))}
                {filtered.length === 0 && (
                  <p style={{ padding: "8px 12px", fontSize: 12, color: "#9ca3af" }}>No assets found</p>
                )}
              </div>
            </div>
          )}
        </div>
        </div>
      }
    >
      <div className="flex flex-col">
        <ResponsiveContainer width="100%" height={420}>
          <LineChart data={apiMonthly} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#6b7280" }} />
            <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} tickFormatter={(v: any) => hoursToHm(Number(v))} label={{ value: "Downtime", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "#6b7280" }, offset: 15 }} />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb", maxHeight: 300, overflowY: "auto" }}
              content={({ active, payload, label }: any) => {
                if (!active || !payload?.length) return null;
                const sorted = [...payload].sort((a: any, b: any) => (b.value || 0) - (a.value || 0));
                return (
                  <div style={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb", background: "#fff", padding: "8px 12px", maxHeight: 300, overflowY: "auto" }}>
                    <p style={{ fontWeight: 600, marginBottom: 4 }}>{label}</p>
                    {sorted.map((item: any) => (
                      <p key={item.dataKey} style={{ color: item.color, margin: "2px 0" }}>
                        {item.name} : {hoursToHm(Number(item.value || 0))}
                      </p>
                    ))}
                  </div>
                );
              }}
            />
            {visibleAssets.map((a) => (
              <Line key={a.key} type="monotone" dataKey={a.key} stroke={a.color} strokeWidth={selectedAsset ? 3 : 2} dot={{ r: selectedAsset ? 4 : 3 }} activeDot={{ r: 5 }} />
            ))}
          </LineChart>
        </ResponsiveContainer>
        <div className="flex flex-wrap gap-x-4 gap-y-1 justify-center pt-2">
          {visibleAssets.map((a) => (
            <div key={a.key} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: a.color }} />
              <span style={{ fontSize: 11, color: "#6b7280" }}>{a.key}</span>
            </div>
          ))}
        </div>
      </div>
    </ChartCard>
  );
}

function AssetMetricsTab({ days, timePeriod }: { days: number; timePeriod: WRTimePeriod }) {
  const dashboardApi = useDashboardApi();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isExportingDownAssetsPDF, setIsExportingDownAssetsPDF] = useState(false);
  const [isExportingDownAssetsXLS, setIsExportingDownAssetsXLS] = useState(false);
  const [isExportingMttrMtbfPDF, setIsExportingMttrMtbfPDF] = useState(false);
  const [isExportingMttrMtbfXLS, setIsExportingMttrMtbfXLS] = useState(false);
  const [isExportingReliabilityPDF, setIsExportingReliabilityPDF] = useState(false);
  const [isExportingReliabilityXLS, setIsExportingReliabilityXLS] = useState(false);
  const [reliabilityData, setReliabilityData] = useState<any[]>([]);
  const [isExportingWarrantiesPDF, setIsExportingWarrantiesPDF] = useState(false);
  const [isExportingWarrantiesXLS, setIsExportingWarrantiesXLS] = useState(false);

  useEffect(() => {
    setLoading(true);
    dashboardApi.getManagerDashboardAssets(
      { Days: days },
      (response: any) => {
        const d = response?.data || response;
        setData(d);
        setLoading(false);
      },
      (error: any) => { console.error("Failed to fetch asset metrics:", error); setLoading(false); }
    );
  }, [days]);

  useEffect(() => {
    dashboardApi.getReliabilityMatrixData(
      (response: any) => { setReliabilityData(response?.data || response || []); },
      (error: any) => { console.error("Failed to fetch reliability matrix:", error); }
    );
  }, []);

  const handleExportDownAssetsPDF = () => {
    setIsExportingDownAssetsPDF(true);
    dashboardApi.exportDownAssetsToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'down_assets'); setIsExportingDownAssetsPDF(false); },
      () => { setIsExportingDownAssetsPDF(false); }
    );
  };
  const handleExportDownAssetsXLS = () => {
    setIsExportingDownAssetsXLS(true);
    dashboardApi.exportDownAssetsToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'down_assets'); setIsExportingDownAssetsXLS(false); },
      () => { setIsExportingDownAssetsXLS(false); }
    );
  };
  const handleExportMttrMtbfPDF = () => {
    setIsExportingMttrMtbfPDF(true);
    dashboardApi.exportMttrMtbfToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'mttr_mtbf_by_asset'); setIsExportingMttrMtbfPDF(false); },
      () => { setIsExportingMttrMtbfPDF(false); }
    );
  };
  const handleExportMttrMtbfXLS = () => {
    setIsExportingMttrMtbfXLS(true);
    dashboardApi.exportMttrMtbfToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'mttr_mtbf_by_asset'); setIsExportingMttrMtbfXLS(false); },
      () => { setIsExportingMttrMtbfXLS(false); }
    );
  };
  const handleExportReliabilityPDF = () => {
    setIsExportingReliabilityPDF(true);
    dashboardApi.exportReliabilityMatrixToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'reliability_matrix'); setIsExportingReliabilityPDF(false); },
      () => { setIsExportingReliabilityPDF(false); }
    );
  };
  const handleExportReliabilityXLS = () => {
    setIsExportingReliabilityXLS(true);
    dashboardApi.exportReliabilityMatrixToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'reliability_matrix'); setIsExportingReliabilityXLS(false); },
      () => { setIsExportingReliabilityXLS(false); }
    );
  };
  const handleExportWarrantiesPDF = () => {
    setIsExportingWarrantiesPDF(true);
    dashboardApi.exportExpiringWarrantiesToPDF(
      { Days: days },
      (resp: any) => { downloadPDF(resp, 'expiring_warranties'); setIsExportingWarrantiesPDF(false); },
      () => { setIsExportingWarrantiesPDF(false); }
    );
  };
  const handleExportWarrantiesXLS = () => {
    setIsExportingWarrantiesXLS(true);
    dashboardApi.exportExpiringWarrantiesToXLS(
      { Days: days },
      (resp: any) => { downloadExcel(resp, 'expiring_warranties'); setIsExportingWarrantiesXLS(false); },
      () => { setIsExportingWarrantiesXLS(false); }
    );
  };

  if (loading) return <WRTabLoader />;

  const downAssets = data?.downAssets || [];
  const expiringWarranties = data?.expiringWarranties || [];
  const apiMttrMtbf = data?.mttrMtbfData || [];
  const chartMttrMtbfData = apiMttrMtbf.map((d: any) => ({ ...d, mtbfScaled: (d.mtbf || 0) / 10 }));

  const avgMttr = chartMttrMtbfData.length > 0
    ? Math.round((chartMttrMtbfData.reduce((sum: number, d: any) => sum + (d.mttr || 0), 0) / chartMttrMtbfData.length) * 10) / 10
    : 0;
  const avgMtbf = chartMttrMtbfData.length > 0
    ? Math.round(chartMttrMtbfData.reduce((sum: number, d: any) => sum + (d.mtbf || 0), 0) / chartMttrMtbfData.length)
    : 0;

  const maxMtbf = Math.max(...chartMttrMtbfData.map((d: any) => d.mtbf || 0), 100);
  const maxMttr = Math.max(...chartMttrMtbfData.map((d: any) => d.mttr || 0), 10);
  const scatterXMax = Math.ceil((maxMtbf + 50) / 50) * 50;
  const scatterYMax = Math.ceil((maxMttr + 5) / 5) * 5;

  const reliabilityChartData = reliabilityData.map((d: any) => {
    const { plottedMtbf, plottedMttr } = clampForPlot(d.mtbf || 0, d.mttr || 0);
    return { ...d, plottedMtbf, plottedMttr };
  });

  const urgencyColors: Record<string, { bg: string; color: string }> = {
    Red: { bg: "#fee2e2", color: "#dc2626" },
    Orange: { bg: "#ffedd5", color: "#ea580c" },
    Yellow: { bg: "#fef9c3", color: "#a16207" },
  };

  const redCount = expiringWarranties.filter((r: any) => r.urgencyLevel === "Red").length;
  const orangeCount = expiringWarranties.filter((r: any) => r.urgencyLevel === "Orange").length;
  const yellowCount = expiringWarranties.filter((r: any) => r.urgencyLevel === "Yellow").length;

  return (
    <div className="flex flex-col gap-5">
      <ChartCard title="Down Assets" subtitle={`Assets currently not in Operational status (excluding Disposed) — ${wrPeriodLabels[timePeriod]}`}
        rightHeader={
          <Dropdown position="end">
            <Dropdown.Button>
              <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                Export
              </button>
            </Dropdown.Button>
            <Dropdown.Content>
              <Menu onClick={handleExportDownAssetsPDF} disabled={isExportingDownAssetsPDF}>
                {isExportingDownAssetsPDF ? "Exporting PDF..." : "PDF"}
              </Menu>
              <Menu onClick={handleExportDownAssetsXLS} disabled={isExportingDownAssetsXLS}>
                {isExportingDownAssetsXLS ? "Exporting XLS..." : "Excel"}
              </Menu>
            </Dropdown.Content>
          </Dropdown>
        }
      >
        {downAssets.length === 0 ? (
          <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "40px 0" }}>All assets are operational</p>
        ) : (
          <>
            <div style={{ overflowX: "auto", maxHeight: 400, overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["Asset Name", "Asset Code", "Facility", "Sub Facility", "Status", "Down Since"].map((h) => (
                      <th key={h} style={{ background: "#f9fafb", fontSize: 12, fontWeight: 600, color: "#6b7280", padding: "10px 16px", textAlign: "left", position: "sticky", top: 0 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {downAssets.map((row: any, i: number) => (
                    <tr key={row.assetID} style={{ background: i % 2 === 1 ? "#fafafa" : "white" }}>
                      <td style={{ fontSize: 13, padding: "10px 16px", color: "#111827" }}>{row.assetName}</td>
                      <td style={{ fontSize: 13, padding: "10px 16px", fontFamily: "monospace", color: "#6b7280" }}>{row.assetCode}</td>
                      <td style={{ fontSize: 13, padding: "10px 16px", color: "#111827" }}>{row.facilityName}</td>
                      <td style={{ fontSize: 13, padding: "10px 16px", color: "#111827" }}>{row.subFacilityName || "—"}</td>
                      <td style={{ fontSize: 13, padding: "10px 16px" }}>
                        <span style={{ background: row.colourCode ? `${row.colourCode}20` : "#fee2e2", color: row.colourCode || "#dc2626", borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>
                          {row.statusName}
                        </span>
                      </td>
                      <td style={{ fontSize: 13, padding: "10px 16px", color: "#6b7280" }}>
                        {row.downSince ? new Date(row.downSince).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ paddingTop: 12, borderTop: "1px solid #f3f4f6", marginTop: 12 }}>
              <span style={{ fontSize: 12, color: "#dc2626" }}>{downAssets.length} asset{downAssets.length !== 1 ? "s" : ""} currently not operational</span>
            </div>
          </>
        )}
      </ChartCard>

      <AssetDowntimeChart days={days} rawDowntimeData={data?.assetDowntimeData || []} />

      <ChartCard title="MTTR & MTBF by Asset" subtitle="Mean Time To Repair vs Mean Time Between Failures — top 15 assets"
        rightHeader={
          <Dropdown position="end">
            <Dropdown.Button>
              <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                Export
              </button>
            </Dropdown.Button>
            <Dropdown.Content>
              <Menu onClick={handleExportMttrMtbfPDF} disabled={isExportingMttrMtbfPDF}>
                {isExportingMttrMtbfPDF ? "Exporting PDF..." : "PDF"}
              </Menu>
              <Menu onClick={handleExportMttrMtbfXLS} disabled={isExportingMttrMtbfXLS}>
                {isExportingMttrMtbfXLS ? "Exporting XLS..." : "Excel"}
              </Menu>
            </Dropdown.Content>
          </Dropdown>
        }
      >
        <div className="flex flex-col">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" style={{ marginBottom: 24 }}>
            <div className="flex items-center gap-3" style={{ background: "#f9fafb", borderRadius: 8, padding: 16 }}>
              <div style={{ width: 3, alignSelf: "stretch", background: "#d97706", borderRadius: 2 }} />
              <div>
                <p style={{ fontSize: 12, color: "#6b7280" }}>Overall Avg MTTR</p>
                <p style={{ fontSize: 28, fontWeight: 600, color: "#111827" }}>{hoursToHm(avgMttr)}</p>
                <p style={{ fontSize: 11, color: "#6b7280" }}>Avg time from asset Down → Closed-Complete</p>
              </div>
            </div>
            <div className="flex items-center gap-3" style={{ background: "#f9fafb", borderRadius: 8, padding: 16 }}>
              <div style={{ width: 3, alignSelf: "stretch", background: "#2563eb", borderRadius: 2 }} />
              <div>
                <p style={{ fontSize: 12, color: "#6b7280" }}>Overall Avg MTBF</p>
                <p style={{ fontSize: 28, fontWeight: 600, color: "#111827" }}>{hoursToHm(avgMtbf)}</p>
                <p style={{ fontSize: 11, color: "#6b7280" }}>Avg time between failures across all assets</p>
              </div>
            </div>
          </div>
          <div className="flex gap-5 mb-3">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: "#d97706" }} />
              <span style={{ fontSize: 12, color: "#6b7280" }}>MTTR</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: "#2563eb" }} />
              <span style={{ fontSize: 12, color: "#6b7280" }}>MTBF ÷ 10</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={360}>
            <BarChart data={chartMttrMtbfData} margin={{ top: 5, right: 10, left: -10, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
              <XAxis dataKey="name" tick={(props: any) => { const { x, y, payload } = props; return (<g transform={`translate(${x},${y})`}><text x={0} y={0} dy={10} textAnchor="end" fill="#6b7280" fontSize={11} transform="rotate(-45)">{payload.value}</text></g>); }} interval={0} height={80} />
              <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} tickFormatter={(v: any) => hoursToHm(Number(v))} label={{ value: "Time (MTBF shown at ÷10 scale)", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "#6b7280" }, offset: 15 }} />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  const item = chartMttrMtbfData.find((d: any) => d.name === label);
                  return (
                    <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 12px", fontSize: 12 }}>
                      <p style={{ fontWeight: 600, marginBottom: 4 }}>{label}</p>
                      <p style={{ color: "#d97706", margin: "2px 0" }}>MTTR: {hoursToHm(Number(item?.mttr || 0))}</p>
                      <p style={{ color: "#2563eb", margin: "2px 0" }}>MTBF: {hoursToHm(Number(item?.mtbf || 0))}</p>
                    </div>
                  );
                }}
              />
              <Bar dataKey="mttr" fill="#d97706" radius={[3, 3, 0, 0]} />
              <Bar dataKey="mtbfScaled" fill="#2563eb" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <p style={{ fontSize: 11, color: "#6b7280", marginTop: 8 }}>Note: MTBF values shown at ÷10 scale for visual comparison. Hover for actual times.</p>
        </div>
      </ChartCard>

      <ChartCard title="Reliability Matrix" subtitle="Asset reliability — MTBF (higher = better) vs MTTR (lower = better). Based on full asset history."
        rightHeader={
          <Dropdown position="end">
            <Dropdown.Button>
              <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                Export
              </button>
            </Dropdown.Button>
            <Dropdown.Content>
              <Menu onClick={handleExportReliabilityPDF} disabled={isExportingReliabilityPDF}>
                {isExportingReliabilityPDF ? "Exporting PDF..." : "PDF"}
              </Menu>
              <Menu onClick={handleExportReliabilityXLS} disabled={isExportingReliabilityXLS}>
                {isExportingReliabilityXLS ? "Exporting XLS..." : "Excel"}
              </Menu>
            </Dropdown.Content>
          </Dropdown>
        }
      >
        <div className="flex flex-col">
          <div className="flex gap-5 mb-3">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#dc2626" }} />
              <span style={{ fontSize: 12, color: "#6b7280" }}>Critical assets (high MTTR, low MTBF)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#d97706" }} />
              <span style={{ fontSize: 12, color: "#6b7280" }}>Watch list (moderate risk)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#16a34a" }} />
              <span style={{ fontSize: 12, color: "#6b7280" }}>Reliable assets (low MTTR, high MTBF)</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={400}>
            <ScatterChart margin={{ top: 20, right: 20, left: 10, bottom: 10 }}>
              <CartesianGrid stroke="#f3f4f6" />
              <XAxis type="number" dataKey="plottedMtbf" domain={[0, 2160]} tick={{ fontSize: 11, fill: "#6b7280" }} label={{ value: "MTBF (hrs) — Higher is better", position: "insideBottom", style: { fontSize: 11, fill: "#6b7280" }, offset: -5 }} />
              <YAxis type="number" dataKey="plottedMttr" domain={[0, 72]} tick={{ fontSize: 11, fill: "#6b7280" }} label={{ value: "MTTR (hrs) — Lower is better", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "#6b7280" }, offset: 0 }} />
              <ZAxis range={[80, 80]} />
              <ReferenceArea x1={0} x2={720} y1={16} y2={72} fill="#fee2e2" fillOpacity={0.12} label={{ value: "Problem assets", position: "insideTopLeft", fill: "#dc2626", fontSize: 11 }} />
              <ReferenceArea x1={720} x2={2160} y1={16} y2={72} fill="transparent" fillOpacity={0} label={{ value: "Investigate", position: "insideTopRight", fill: "#d97706", fontSize: 11 }} />
              <ReferenceArea x1={0} x2={720} y1={0} y2={16} fill="transparent" fillOpacity={0} label={{ value: "Watch list", position: "insideBottomLeft", fill: "#d97706", fontSize: 11 }} />
              <ReferenceArea x1={720} x2={2160} y1={0} y2={16} fill="#dcfce7" fillOpacity={0.12} label={{ value: "Ideal assets", position: "insideBottomRight", fill: "#16a34a", fontSize: 11 }} />
              <ReferenceLine x={720} stroke="#d1d5db" strokeDasharray="4 4" />
              <ReferenceLine x={1440} stroke="#d1d5db" strokeDasharray="4 4" />
              <ReferenceLine y={16} stroke="#d1d5db" strokeDasharray="4 4" />
              <ReferenceLine y={40} stroke="#d1d5db" strokeDasharray="4 4" />
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0]?.payload;
                  return (
                    <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 12px", fontSize: 12 }}>
                      <p style={{ fontWeight: 600, marginBottom: 4 }}>{d?.name}</p>
                      <p style={{ margin: "2px 0" }}>MTBF: {hoursToHm(Number(d?.mtbf || 0))}</p>
                      <p style={{ margin: "2px 0" }}>MTTR: {hoursToHm(Number(d?.mttr || 0))}</p>
                      <p style={{ margin: "2px 0", color: "#6b7280" }}>WRs raised: {d?.wrs}</p>
                    </div>
                  );
                }}
              />
              {reliabilityChartData.map((d: any) => (
                <Scatter key={d.name} data={[d]} fill={getReliabilityColor(d.mttr, d.mtbf)}>
                  <Cell fill={getReliabilityColor(d.mttr, d.mtbf)} />
                </Scatter>
              ))}
            </ScatterChart>
          </ResponsiveContainer>
          <p style={{ fontSize: 11, color: "#6b7280", marginTop: 8 }}>Dashed lines show fixed thresholds — MTBF: 720h, 1440h · MTTR: 16h, 40h</p>
        </div>
      </ChartCard>

      <ChartCard title="Expiring Warranties" subtitle={`Assets with warranties expiring in the next 90 days — ${wrPeriodLabels[timePeriod]}`}
        rightHeader={
          <Dropdown position="end">
            <Dropdown.Button>
              <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #16a34a", background: "#16a34a", cursor: "pointer", color: "#fff" }}>
                Export
              </button>
            </Dropdown.Button>
            <Dropdown.Content>
              <Menu onClick={handleExportWarrantiesPDF} disabled={isExportingWarrantiesPDF}>
                {isExportingWarrantiesPDF ? "Exporting PDF..." : "PDF"}
              </Menu>
              <Menu onClick={handleExportWarrantiesXLS} disabled={isExportingWarrantiesXLS}>
                {isExportingWarrantiesXLS ? "Exporting XLS..." : "Excel"}
              </Menu>
            </Dropdown.Content>
          </Dropdown>
        }
      >
        {expiringWarranties.length === 0 ? (
          <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "40px 0" }}>No warranties expiring in the next 90 days</p>
        ) : (
          <>
            <div style={{ overflowX: "auto", maxHeight: 400, overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["Asset Name", "Asset Code", "Facility", "Warranty Expiry", "Days Left", "Urgency"].map((h) => (
                      <th key={h} style={{ background: "#f9fafb", fontSize: 12, fontWeight: 600, color: "#6b7280", padding: "10px 16px", textAlign: "left", position: "sticky", top: 0 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {expiringWarranties.map((row: any, i: number) => {
                    const uc = urgencyColors[row.urgencyLevel] || urgencyColors.Yellow;
                    return (
                      <tr key={row.assetID} style={{ background: i % 2 === 1 ? "#fafafa" : "white" }}>
                        <td style={{ fontSize: 13, padding: "10px 16px" }}>
                          <span style={{ color: "#2563eb", cursor: "pointer" }} className="hover:underline">{row.assetName}</span>
                        </td>
                        <td style={{ fontSize: 13, padding: "10px 16px", fontFamily: "monospace", color: "#6b7280" }}>{row.assetCode}</td>
                        <td style={{ fontSize: 13, padding: "10px 16px", color: "#111827" }}>{row.facilityName}</td>
                        <td style={{ fontSize: 13, padding: "10px 16px", color: uc.color, fontWeight: row.urgencyLevel === "Red" ? 600 : 400 }}>
                          {new Date(row.warrantyEndDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                        </td>
                        <td style={{ fontSize: 13, padding: "10px 16px", color: uc.color, fontWeight: 600 }}>{row.daysUntilExpiry}d</td>
                        <td style={{ fontSize: 13, padding: "10px 16px" }}>
                          <span style={{ background: uc.bg, color: uc.color, borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>
                            {row.urgencyLevel === "Red" ? "Urgent" : row.urgencyLevel === "Orange" ? "Soon" : "Upcoming"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ paddingTop: 12, borderTop: "1px solid #f3f4f6", marginTop: 12 }}>
              <span style={{ fontSize: 12 }}>
                {redCount > 0 && <span style={{ color: "#dc2626" }}>{redCount} urgent (≤15d)</span>}
                {redCount > 0 && (orangeCount > 0 || yellowCount > 0) && <span style={{ color: "#6b7280" }}> · </span>}
                {orangeCount > 0 && <span style={{ color: "#ea580c" }}>{orangeCount} soon (≤30d)</span>}
                {orangeCount > 0 && yellowCount > 0 && <span style={{ color: "#6b7280" }}> · </span>}
                {yellowCount > 0 && <span style={{ color: "#a16207" }}>{yellowCount} upcoming (≤90d)</span>}
              </span>
            </div>
          </>
        )}
      </ChartCard>
    </div>
  );
}

const ManagerDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState(tabs[0]);
  const [timePeriod, setTimePeriod] = useState<WRTimePeriod>("30D");
  const days = wrPeriodToDays[timePeriod];

  return (
    <Layout>
      <Header title="Manager Dashboard">
        <Header.Right>
          <WRTimeToggle active={timePeriod} onChange={setTimePeriod} />
        </Header.Right>
      </Header>
      <div className="p-4">
        <div
          className="bg-white border-b shrink-0"
          style={{ borderColor: "#e5e7eb", padding: "0 24px" }}
        >
          <div className="flex">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className="relative transition-colors"
                style={{
                  padding: "12px 16px",
                  fontSize: 14,
                  fontWeight: activeTab === tab ? 600 : 400,
                  color: activeTab === tab ? "#2B5A2E" : "#6b7280",
                  borderBottom: activeTab === tab ? "2px solid #2B5A2E" : "2px solid transparent",
                  background: "none",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== tab) e.currentTarget.style.color = "#111827";
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab) e.currentTarget.style.color = "#6b7280";
                }}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        <div style={{ background: "#f9fafb", padding: 24, flex: 1 }}>
          {activeTab === "Work Requests & Tasks" && <WorkRequestsTab days={days} timePeriod={timePeriod} />}
          {activeTab === "Team Metrics" && <TeamMetricsTab days={days} timePeriod={timePeriod} />}
          {activeTab === "Purchasing" && <PurchasingTab days={days} timePeriod={timePeriod} />}
          {activeTab === "Asset Metrics" && <AssetMetricsTab days={days} timePeriod={timePeriod} />}
          {activeTab !== "Work Requests & Tasks" && activeTab !== "Team Metrics" && activeTab !== "Purchasing" && activeTab !== "Asset Metrics" && (
            <div
              className="bg-white rounded-lg text-center"
              style={{ padding: 40, borderRadius: 8 }}
            >
              <p style={{ color: "#9ca3af", fontSize: 14 }}>
                {activeTab} charts coming soon
              </p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ManagerDashboard;
